import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import api, { getData, postData, putData, patchData, deleteData } from '../../../services/api';
import PageHeader from '../../../components/common/PageHeader';
import AlertToast from '../../../components/common/AlertToast';
import './CertificatesPage.css';

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const toInputDate = (value) => {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toISOString().split('T')[0];
};

const STATUS_BS = { issued: 'success', revoked: 'danger' };
const VIA_LABEL = {
  manual: 'Manual',
  auto_enrollment: 'Auto (Enrollment)',
  auto_batch: 'Auto (Batch)',
  bulk: 'Bulk',
};
const BUILTIN_CERT_KEYS = new Set([
  'studentName', 'studentEmail', 'courseName', 'courseCode', 'completionDate',
  'certificateNumber', 'grade', 'batchNumber', 'enrollmentNumber',
  'tenantName', 'tenantLogoUrl', 'issuerName', 'issuerTitle',
  'verifyUrl', 'qrDataUrl', 'certificateTitle', 'subtitle',
]);

const buildCertificateEditorFallbackTemplate = () => ({
  name: 'Recovered Template',
  description: 'Fallback template used because the original template link is unavailable.',
  orientation: 'landscape',
  widthPx: 1123,
  heightPx: 794,
  backgroundType: 'image',
  backgroundColor: '#ffffff',
  backgroundAsset: '',
  htmlBody: '',
  cssStyles: '',
  fields: [
    { _id: `fallback-${Date.now()}-1`, type: 'text', key: 'certificateTitle', label: 'Certificate Title', x: 20, y: 14, w: 60, h: 8, fontSize: 26, fontFamily: 'Georgia, serif', fontWeight: 'bold', fontStyle: 'normal', color: '#1a1a1a', align: 'center', lineHeight: 1.2, letterSpacing: 0, rotation: 0, backgroundColor: 'transparent', borderRadius: 0, opacity: 1, zIndex: 2, srcKey: '', maxStars: 5, isStatic: false },
    { _id: `fallback-${Date.now()}-2`, type: 'text', key: 'studentName', label: 'Student Name', x: 15, y: 36, w: 70, h: 12, fontSize: 40, fontFamily: 'Georgia, serif', fontWeight: 'bold', fontStyle: 'normal', color: '#1a1a1a', align: 'center', lineHeight: 1.1, letterSpacing: 0, rotation: 0, backgroundColor: 'transparent', borderRadius: 0, opacity: 1, zIndex: 2, srcKey: '', maxStars: 5, isStatic: false },
    { _id: `fallback-${Date.now()}-3`, type: 'text', key: 'courseName', label: 'Course Name', x: 18, y: 54, w: 64, h: 8, fontSize: 22, fontFamily: 'Georgia, serif', fontWeight: 'bold', fontStyle: 'italic', color: '#444444', align: 'center', lineHeight: 1.2, letterSpacing: 0, rotation: 0, backgroundColor: 'transparent', borderRadius: 0, opacity: 1, zIndex: 2, srcKey: '', maxStars: 5, isStatic: false },
    { _id: `fallback-${Date.now()}-4`, type: 'text', key: 'batchNumber', label: 'Batch Number', x: 12, y: 70, w: 28, h: 5, fontSize: 14, fontFamily: 'Arial, sans-serif', fontWeight: 'bold', fontStyle: 'normal', color: '#1a1a1a', align: 'center', lineHeight: 1.2, letterSpacing: 0, rotation: 0, backgroundColor: 'transparent', borderRadius: 0, opacity: 1, zIndex: 2, srcKey: '', maxStars: 5, isStatic: false },
    { _id: `fallback-${Date.now()}-5`, type: 'text', key: 'completionDate', label: 'Completion Date', x: 60, y: 70, w: 28, h: 5, fontSize: 14, fontFamily: 'Arial, sans-serif', fontWeight: 'normal', fontStyle: 'normal', color: '#333333', align: 'center', lineHeight: 1.2, letterSpacing: 0, rotation: 0, backgroundColor: 'transparent', borderRadius: 0, opacity: 1, zIndex: 2, srcKey: '', maxStars: 5, isStatic: false },
    { _id: `fallback-${Date.now()}-6`, type: 'text', key: 'certificateNumber', label: 'Certificate Number', x: 34, y: 88, w: 32, h: 4, fontSize: 11, fontFamily: 'Courier New, monospace', fontWeight: 'normal', fontStyle: 'normal', color: '#555555', align: 'center', lineHeight: 1.2, letterSpacing: 1, rotation: 0, backgroundColor: 'transparent', borderRadius: 0, opacity: 1, zIndex: 2, srcKey: '', maxStars: 5, isStatic: false },
  ],
});

const buildDefaultCertificateImageFields = () => ([
  { _id: `seed-${Date.now()}-1`, type: 'text', key: 'studentName', label: 'Student Name', x: 15, y: 38, w: 70, h: 12, fontSize: 44, fontFamily: 'Georgia, serif', fontWeight: 'bold', fontStyle: 'normal', color: '#1a1a1a', align: 'center', lineHeight: 1.1, letterSpacing: 0, rotation: 0, backgroundColor: 'transparent', borderRadius: 0, opacity: 1, zIndex: 2, srcKey: '', maxStars: 5, isStatic: false },
  { _id: `seed-${Date.now()}-2`, type: 'text', key: 'courseName', label: 'Course Name', x: 20, y: 56, w: 60, h: 8, fontSize: 24, fontFamily: 'Georgia, serif', fontWeight: 'bold', fontStyle: 'italic', color: '#444444', align: 'center', lineHeight: 1.2, letterSpacing: 0, rotation: 0, backgroundColor: 'transparent', borderRadius: 0, opacity: 1, zIndex: 2, srcKey: '', maxStars: 5, isStatic: false },
  { _id: `seed-${Date.now()}-3`, type: 'text', key: 'batchNumber', label: 'Batch No.', x: 12, y: 70, w: 28, h: 5, fontSize: 14, fontFamily: 'Arial, sans-serif', fontWeight: 'bold', fontStyle: 'normal', color: '#1a1a1a', align: 'center', lineHeight: 1.2, letterSpacing: 0, rotation: 0, backgroundColor: 'transparent', borderRadius: 0, opacity: 1, zIndex: 2, srcKey: '', maxStars: 5, isStatic: false },
  { _id: `seed-${Date.now()}-4`, type: 'text', key: 'enrollmentNumber', label: 'Enrollment No.', x: 60, y: 70, w: 28, h: 5, fontSize: 14, fontFamily: 'Arial, sans-serif', fontWeight: 'bold', fontStyle: 'normal', color: '#1a1a1a', align: 'center', lineHeight: 1.2, letterSpacing: 0, rotation: 0, backgroundColor: 'transparent', borderRadius: 0, opacity: 1, zIndex: 2, srcKey: '', maxStars: 5, isStatic: false },
  { _id: `seed-${Date.now()}-5`, type: 'text', key: 'completionDate', label: 'Completion Date', x: 12, y: 82, w: 25, h: 5, fontSize: 14, fontFamily: 'Arial, sans-serif', fontWeight: 'normal', fontStyle: 'normal', color: '#333333', align: 'center', lineHeight: 1.2, letterSpacing: 0, rotation: 0, backgroundColor: 'transparent', borderRadius: 0, opacity: 1, zIndex: 2, srcKey: '', maxStars: 5, isStatic: false },
  { _id: `seed-${Date.now()}-6`, type: 'text', key: 'certificateNumber', label: 'Certificate Number', x: 37, y: 90, w: 26, h: 4, fontSize: 11, fontFamily: 'Courier New, monospace', fontWeight: 'normal', fontStyle: 'normal', color: '#555555', align: 'center', lineHeight: 1.2, letterSpacing: 1, rotation: 0, backgroundColor: 'transparent', borderRadius: 0, opacity: 1, zIndex: 2, srcKey: '', maxStars: 5, isStatic: false },
  { _id: `seed-${Date.now()}-7`, type: 'text', key: 'grade', label: 'Grade', x: 63, y: 82, w: 25, h: 5, fontSize: 16, fontFamily: 'Arial, sans-serif', fontWeight: 'bold', fontStyle: 'normal', color: '#b68d40', align: 'center', lineHeight: 1.2, letterSpacing: 0, rotation: 0, backgroundColor: 'transparent', borderRadius: 0, opacity: 1, zIndex: 2, srcKey: '', maxStars: 5, isStatic: false },
]);

const BSModal = ({ show, onClose, title, size = '', children, footer }) =>
  show ? (
    <div className="modal fade show d-block" style={{ background: 'rgba(0,0,0,0.45)', zIndex: 1050 }}
         onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={`modal-dialog modal-dialog-scrollable modal-dialog-centered ${size}`}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{title}</h5>
            <button className="btn-close" onClick={onClose} />
          </div>
          <div className="modal-body">{children}</div>
          {footer && <div className="modal-footer">{footer}</div>}
        </div>
      </div>
    </div>
  ) : null;

// ─── Main Page ────────────────────────────────────────────────────────────────

// Detects whether the current tenant is an education tenant. Used by every
// sub-component on this page so labels switch from "Course/Batch/Trainee" to
// "Subject/Class/Student" automatically.
function useTenantLabels() {
  const { tenant } = useAuth();
  const slug = (() => {
    if (!tenant) return '';
    const refs = [tenant.industryId, tenant.industry, tenant.industrySlug];
    for (const ref of refs) {
      if (typeof ref === 'string' && ref) return ref.toLowerCase();
      if (ref && typeof ref === 'object') {
        if (typeof ref.slug === 'string') return ref.slug.toLowerCase();
        if (typeof ref.name === 'string') return ref.name.toLowerCase();
      }
    }
    return '';
  })();
  const isEdu = slug === 'education' || slug === 'school';
  return {
    isEdu,
    course:        isEdu ? 'Course / Programme'    : 'Course',
    courseName:    isEdu ? 'Course / Programme'    : 'Course Name',
    courseLower:   isEdu ? 'course / programme'    : 'course',
    batch:         isEdu ? 'Class'                  : 'Batch',
    batchNumber:   isEdu ? 'Class & Section'        : 'Batch Number',
    batchLower:    isEdu ? 'class'                  : 'batch',
    trainee:       isEdu ? 'student'                : 'trainee',
    issueForBatch: isEdu ? 'Issue for Class'        : 'Issue for Batch',
    selectBatch:   isEdu ? 'Select a class'         : 'Select a batch',
    autoBatch:     isEdu ? 'Auto (Class)'           : 'Auto (Batch)',
    completion:    isEdu
      ? 'Issues certificates for every student who has completed the academic year / course in the selected class. Already-issued students are skipped (unless "Force" is checked).'
      : 'Issues certificates for all completed enrollments in the selected batch. Enrollments that already have a certificate are skipped (unless "Force" is checked).',
    bulkEmailHint: isEdu
      ? 'Emails all issued certificates in this class to the students\' registered email addresses. Requires SMTP to be configured in the backend .env.'
      : 'Emails all issued certificates in this batch to the students\' registered email addresses. Requires SMTP to be configured in the backend .env.',
    manualHint:    isEdu
      ? 'Enter student and course details directly. No enrollment record is required — use this for transfer certificates, bonafide certificates, or one-off issuances.'
      : 'Enter student and course details directly. No enrollment record is required — use this for guest programmes, external trainees, or one-off certificates.',
    subtitle: isEdu
      ? 'Issue, manage, and verify student certificates (promotion, transfer, bonafide, course completion).'
      : 'Issue, manage, and verify student certificates',
  };
}

export default function CertificatesPage() {
  const { user } = useAuth();
  const role = user?.roleKey || user?.role || '';
  const canWrite = ['admin', 'manager', 'team_lead'].includes(role);
  const canAdmin = role === 'admin';
  const T = useTenantLabels();

  const [tab, setTab] = useState('records');
  const [alert, setAlert] = useState({ show: false, msg: '', type: 'success' });
  const showAlert = (msg, type = 'success') => setAlert({ show: true, msg, type });

  if (role === 'student') return <StudentCertificateLibrary />;

  return (
    <div className="container-fluid py-3">
      <PageHeader
        title="Certificates"
        icon="bi-patch-check"
        subtitle={T.subtitle}
      />

      <ul className="nav nav-tabs mb-3">
        <li className="nav-item">
          <button className={`nav-link ${tab === 'records' ? 'active' : ''}`} onClick={() => setTab('records')}>
            <i className="bi bi-award me-2" />Issued Certificates
          </button>
        </li>
        <li className="nav-item">
          <button className={`nav-link ${tab === 'templates' ? 'active' : ''}`} onClick={() => setTab('templates')}>
            <i className="bi bi-palette me-2" />Templates
          </button>
        </li>
      </ul>

      {tab === 'records' && <RecordsTab canWrite={canWrite} canAdmin={canAdmin} showAlert={showAlert} />}
      {tab === 'templates' && <TemplatesTab canWrite={canWrite} canAdmin={canAdmin} showAlert={showAlert} />}

      <AlertToast show={alert.show} message={alert.msg} type={alert.type} onClose={() => setAlert({ ...alert, show: false })} />
    </div>
  );
}

function StudentCertificateLibrary() {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');

  const loadCertificates = useCallback(async () => {
    setLoading(true);
    setMessage('');
    try {
      const data = await getData('/training/certificates', { page: 1, limit: 100 });
      setRecords(data.records || []);
    } catch (err) {
      setRecords([]);
      setMessage(err.response?.data?.message || 'Could not load your certificates.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadCertificates(); }, [loadCertificates]);

  const openCertificate = async (record, download = false) => {
    try {
      const response = await api.get(`/training/certificates/${record._id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(response.data);
      if (download) {
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = `${record.certificateNumber || 'certificate'}.pdf`;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
      } else {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
      window.setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (err) {
      setMessage(err.response?.data?.message || 'Could not open this certificate.');
    }
  };

  const issuedRecords = records.filter((record) => record.status === 'issued');
  const revokedRecords = records.filter((record) => record.status === 'revoked');

  return (
    <div className="student-certificate-page">
      <header className="student-certificate-header">
        <button type="button" onClick={() => navigate('/lms?tab=my-subscription')} aria-label="Back to Subscription"><i className="bi bi-arrow-left" /></button>
        <span><i className="bi bi-award" /></span>
        <div><small>Learning achievements</small><h1>My Certificates</h1><p>View and download certificates issued to your student account.</p></div>
        <button type="button" className="student-certificate-refresh" onClick={loadCertificates} disabled={loading}><i className="bi bi-arrow-clockwise" /> Refresh</button>
      </header>

      <section className="student-certificate-summary">
        <div><span><i className="bi bi-patch-check-fill" /></span><strong>{issuedRecords.length}</strong><small>Issued</small></div>
        <div><span><i className="bi bi-journal-check" /></span><strong>{records.length}</strong><small>Total records</small></div>
        <div><span><i className="bi bi-shield-check" /></span><strong>{revokedRecords.length}</strong><small>Revoked</small></div>
        <p><i className="bi bi-info-circle" /> Certificates appear here after they are officially issued for your completed course or program.</p>
      </section>

      {message && <div className="student-certificate-message"><i className="bi bi-exclamation-circle" />{message}</div>}
      {loading ? (
        <div className="student-certificate-empty"><span className="spinner-border spinner-border-sm" /><p>Loading certificates...</p></div>
      ) : records.length ? (
        <div className="student-certificate-grid">
          {records.map((record) => {
            const courseName = record.courseId?.courseName || record.data?.courseName || 'Course completion';
            return (
              <article key={record._id} className={record.status === 'revoked' ? 'is-revoked' : ''}>
                <div className="student-certificate-art"><i className="bi bi-award-fill" /><span>{record.status === 'issued' ? 'Verified achievement' : 'Certificate record'}</span></div>
                <div className="student-certificate-body">
                  <span className={`student-certificate-status is-${record.status || 'issued'}`}><i className="bi bi-circle-fill" />{record.status || 'issued'}</span>
                  <h2>{courseName}</h2>
                  <p>Certificate No. <strong>{record.certificateNumber || '-'}</strong></p>
                  <div className="student-certificate-meta"><span><i className="bi bi-calendar3" />Issued {fmtDate(record.issuedAt)}</span>{(record.batchId?.batchNumber || record.data?.batchNumber) && <span><i className="bi bi-people" />{record.batchId?.batchNumber || record.data?.batchNumber}</span>}</div>
                  <div className="student-certificate-actions">
                    <button type="button" onClick={() => openCertificate(record)} disabled={record.status === 'revoked'}><i className="bi bi-eye" />Preview</button>
                    <button type="button" className="is-primary" onClick={() => openCertificate(record, true)} disabled={record.status === 'revoked'}><i className="bi bi-download" />Download</button>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      ) : (
        <div className="student-certificate-empty"><i className="bi bi-award" /><h2>No certificates issued yet</h2><p>Complete eligible courses and wait for the institution to issue your certificate.</p><button type="button" className="btn btn-primary" onClick={() => navigate('/lms?tab=my-courses')}>Continue learning</button></div>
      )}
    </div>
  );
}

// ─── Records Tab ──────────────────────────────────────────────────────────────

function RecordsTab({ canWrite, canAdmin, showAlert }) {
  const T = useTenantLabels();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({ status: '', batch: '', search: '' });

  const [batches, setBatches] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [templates, setTemplates] = useState([]);

  const [showIssue, setShowIssue] = useState(false);
  const [showBulkIssue, setShowBulkIssue] = useState(false);
  const [showBulkEmail, setShowBulkEmail] = useState(false);
  const [previewing, setPreviewing] = useState(null);
  const [revoking, setRevoking] = useState(null);
  const [regenerating, setRegenerating] = useState(null);
  const [editingRecord, setEditingRecord] = useState(null);
  const [designingRecord, setDesigningRecord] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getData('/training/certificates', { page, limit: 20, ...filters });
      setRecords(res.records || []);
      setTotalPages(res.totalPages || 1);
    } catch (e) {
      showAlert(e.response?.data?.message || 'Failed to load certificates', 'danger');
    } finally { setLoading(false); }
  }, [page, filters, showAlert]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    (async () => {
      try {
        const [b, t] = await Promise.all([
          getData('/training/batches', { limit: 200 }),
          getData('/training/certificates/templates'),
        ]);
        setBatches(b.records || b.data || []);
        setTemplates(t.records || t.data || []);
      } catch (_) {}
    })();
  }, []);

  const loadEnrollmentsForIssue = async () => {
    try {
      // Pull all enrollments, then filter out terminated ones client-side.
      // Showing only `completed` was too strict — interim certificates are common
      // (e.g. ongoing batches, internships, partial programmes).
      const res = await getData('/training/enrollments', { limit: 200 });
      const list = res.records || res.data || [];
      const eligible = list.filter(e => !['cancelled', 'discontinued'].includes(e.status));
      setEnrollments(eligible);
    } catch (e) { showAlert('Failed to load enrollments', 'danger'); }
  };

  const handleDownload = async (rec) => {
    try {
      const res = await api.get(`/training/certificates/${rec._id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${rec.certificateNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) { showAlert('Download failed', 'danger'); }
  };

  const handleEmail = async (rec) => {
    if (!window.confirm(`Email this certificate to ${rec.data?.studentEmail || 'the student'}?`)) return;
    try {
      await postData(`/training/certificates/${rec._id}/email`);
      showAlert('Certificate emailed successfully');
      load();
    } catch (e) { showAlert(e.response?.data?.message || 'Email failed', 'danger'); }
  };

  const handlePreview = async (rec) => {
    try {
      const res = await api.get(`/training/certificates/${rec._id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      setPreviewing({ rec, url });
    } catch (e) { showAlert('Preview failed', 'danger'); }
  };

  const handleRevoke = async (reason) => {
    try {
      await patchData(`/training/certificates/${revoking._id}/revoke`, { reason });
      showAlert('Certificate revoked');
      setRevoking(null);
      load();
    } catch (e) { showAlert(e.response?.data?.message || 'Revoke failed', 'danger'); }
  };

  return (
    <>
      <div className="card">
        <div className="card-body">
          {/* Filters + actions row */}
          <div className="row g-2 mb-3 align-items-end">
            <div className="col-md-3">
              <label className="form-label small mb-1">Search Cert No.</label>
              <input className="form-control form-control-sm" value={filters.search}
                     onChange={e => { setFilters(f => ({ ...f, search: e.target.value })); setPage(1); }} />
            </div>
            <div className="col-md-2">
              <label className="form-label small mb-1">Status</label>
              <select className="form-select form-select-sm" value={filters.status}
                      onChange={e => { setFilters(f => ({ ...f, status: e.target.value })); setPage(1); }}>
                <option value="">All</option>
                <option value="issued">Issued</option>
                <option value="revoked">Revoked</option>
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label small mb-1">{T.batch}</label>
              <select className="form-select form-select-sm" value={filters.batch}
                      onChange={e => { setFilters(f => ({ ...f, batch: e.target.value })); setPage(1); }}>
                <option value="">All</option>
                {batches.map(b => <option key={b._id} value={b._id}>{b.batchNumber}</option>)}
              </select>
            </div>
            <div className="col-md-4 text-end">
              {canWrite && <>
                <button className="btn btn-sm btn-outline-primary me-2" onClick={() => { loadEnrollmentsForIssue(); setShowIssue(true); }}>
                  <i className="bi bi-plus-circle me-1" />Issue
                </button>
                <button className="btn btn-sm btn-outline-success me-2" onClick={() => setShowBulkIssue(true)}>
                  <i className="bi bi-collection me-1" />Bulk Issue
                </button>
                <button className="btn btn-sm btn-outline-info" onClick={() => setShowBulkEmail(true)}>
                  <i className="bi bi-envelope me-1" />Bulk Email
                </button>
              </>}
            </div>
          </div>

          {loading ? (
            <div className="text-center py-5"><div className="spinner-border text-primary" /></div>
          ) : records.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <i className="bi bi-patch-check fs-1 d-block mb-2" />
              No certificates issued yet
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm table-hover align-middle">
                <thead className="table-light">
                  <tr>
                    <th>Certificate No.</th>
                    <th>Student</th>
                    <th>{T.course}</th>
                    <th>{T.batch}</th>
                    <th>Issued</th>
                    <th>Via</th>
                    <th>Status</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {records.map(r => (
                    <tr key={r._id}>
                      <td className="font-monospace small">{r.certificateNumber}</td>
                      <td>
                        {r.studentId?.firstName} {r.studentId?.lastName}
                        <div className="text-muted small">{r.studentId?.email || r.data?.studentEmail}</div>
                      </td>
                      <td>{r.courseId?.courseName || r.data?.courseName || '—'}</td>
                      <td>{r.batchId?.batchNumber || r.data?.batchNumber || '—'}</td>
                      <td className="small">{fmtDate(r.issuedAt)}</td>
                      <td><span className="badge bg-light text-dark small">{
                        r.issuedVia === 'auto_batch' ? T.autoBatch : (VIA_LABEL[r.issuedVia] || r.issuedVia)
                      }</span></td>
                      <td>
                        <span className={`badge bg-${STATUS_BS[r.status] || 'secondary'}`}>{r.status}</span>
                        {r.emailSentAt && <i className="bi bi-envelope-check text-success ms-2" title={`Emailed ${fmtDate(r.emailSentAt)}`} />}
                      </td>
                      <td className="text-end">
                        <button className="btn btn-sm btn-outline-secondary me-1" title="Preview" onClick={() => handlePreview(r)}>
                          <i className="bi bi-eye" />
                        </button>
                        <button className="btn btn-sm btn-outline-primary me-1" title="Download" onClick={() => handleDownload(r)}>
                          <i className="bi bi-download" />
                        </button>
                        {canWrite && r.status === 'issued' && (
                          <button className="btn btn-sm btn-outline-success me-1" title="Edit" onClick={() => setEditingRecord(r)}>
                            <i className="bi bi-pencil" />
                          </button>
                        )}
                        {canWrite && r.status === 'issued' && (
                          <button className="btn btn-sm btn-outline-dark me-1" title="Design Edit" onClick={() => setDesigningRecord(r)}>
                            <i className="bi bi-bounding-box" />
                          </button>
                        )}
                        {canWrite && r.status === 'issued' && (
                          <button className="btn btn-sm btn-outline-info me-1" title="Email" onClick={() => handleEmail(r)}>
                            <i className="bi bi-envelope" />
                          </button>
                        )}
                        {canWrite && r.status === 'issued' && (
                          <button className="btn btn-sm btn-outline-warning me-1" title="Regenerate (revoke old, re-issue with current template)"
                                  onClick={() => setRegenerating(r)}>
                            <i className="bi bi-arrow-repeat" />
                          </button>
                        )}
                        {canAdmin && r.status === 'issued' && (
                          <button className="btn btn-sm btn-outline-danger" title="Revoke" onClick={() => setRevoking(r)}>
                            <i className="bi bi-x-circle" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {totalPages > 1 && (
            <div className="d-flex justify-content-center mt-3">
              <div className="btn-group">
                <button className="btn btn-sm btn-outline-secondary" disabled={page === 1} onClick={() => setPage(p => p - 1)}>Prev</button>
                <span className="btn btn-sm btn-light disabled">Page {page} of {totalPages}</span>
                <button className="btn btn-sm btn-outline-secondary" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Issue single */}
      <IssueDialog
        show={showIssue}
        onClose={() => setShowIssue(false)}
        enrollments={enrollments}
        templates={templates}
        onIssued={() => { setShowIssue(false); load(); showAlert('Certificate issued'); }}
        onError={(m) => showAlert(m, 'danger')}
      />

      {/* Bulk issue */}
      <BulkIssueDialog
        show={showBulkIssue}
        onClose={() => setShowBulkIssue(false)}
        batches={batches}
        templates={templates}
        onDone={(res) => { setShowBulkIssue(false); load(); showAlert(`Bulk: ${res.issued} issued, ${res.skipped} skipped, ${res.failed} failed`, res.failed ? 'warning' : 'success'); }}
        onError={(m) => showAlert(m, 'danger')}
      />

      {/* Bulk email */}
      <BulkEmailDialog
        show={showBulkEmail}
        onClose={() => setShowBulkEmail(false)}
        batches={batches}
        onDone={(res) => { setShowBulkEmail(false); load(); showAlert(`Emailed ${res.sent} (${res.failed} failed)`, res.failed ? 'warning' : 'success'); }}
        onError={(m) => showAlert(m, 'danger')}
      />

      <EditCertificateRecordDialog
        show={!!editingRecord}
        record={editingRecord}
        templates={templates}
        onClose={() => setEditingRecord(null)}
        onSaved={() => {
          setEditingRecord(null);
          load();
          showAlert('Certificate updated');
        }}
        onError={(m) => showAlert(m, 'danger')}
      />

      <CertificateDesignEditorDialog
        show={!!designingRecord}
        record={designingRecord}
        onClose={() => setDesigningRecord(null)}
        onSaved={() => {
          setDesigningRecord(null);
          load();
          showAlert('Certificate design updated');
        }}
        onError={(m) => showAlert(m, 'danger')}
      />

      {/* Preview */}
      <BSModal show={!!previewing} onClose={() => { if (previewing) URL.revokeObjectURL(previewing.url); setPreviewing(null); }}
               title={previewing ? `Preview — ${previewing.rec.certificateNumber}` : ''}
               size="modal-xl">
        {previewing && (
          <iframe src={previewing.url} title="Certificate Preview" style={{ width: '100%', height: '70vh', border: '1px solid #ddd' }} />
        )}
      </BSModal>

      {/* Revoke confirm */}
      <RevokeDialog
        record={revoking}
        onClose={() => setRevoking(null)}
        onConfirm={handleRevoke}
      />

      {/* Regenerate */}
      <RegenerateDialog
        record={regenerating}
        templates={templates}
        onClose={() => setRegenerating(null)}
        onDone={(newCertNumber) => {
          setRegenerating(null);
          showAlert(`Regenerated as ${newCertNumber}. Old certificate revoked.`);
          load();
        }}
        onError={(m) => showAlert(m, 'danger')}
      />
    </>
  );
}

// ─── Issue single dialog ──────────────────────────────────────────────────────

function IssueDialog({ show, onClose, enrollments, templates, onIssued, onError }) {
  const T = useTenantLabels();
  // Mode controls where student data comes from:
  //   'enrollment' → training tenants pick a completed enrollment (existing path)
  //   'student'    → education tenants pick from student_management records
  //   'manual'     → both: type details directly (transfer/bonafide/external)
  const [mode, setMode] = useState(T.isEdu ? 'student' : 'enrollment');
  const [enrollmentId, setEnrollmentId] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [grade, setGrade] = useState('');
  const [manual, setManual] = useState({
    studentName: '', studentEmail: '', courseName: '',
    batchNumber: '', completionDate: '', grade: '',
    issuerName: '', issuerTitle: '', certificateTitle: '', subtitle: '',
  });
  const [force, setForce] = useState(false);
  const [busy, setBusy] = useState(false);
  const [extraIssueFields, setExtraIssueFields] = useState([]);
  const [hiddenTemplateFields, setHiddenTemplateFields] = useState([]);
  const [newField, setNewField] = useState({ label: '', key: '', type: 'text', maxStars: 5 });

  // Student picker (education tenants) — pulls from student_management.
  const [students, setStudents]       = useState([]);
  const [studentRecordId, setStudentRecordId] = useState('');
  const [studentLoading, setStudentLoading]   = useState(false);
  const [studentSearch, setStudentSearch]     = useState('');

  useEffect(() => {
    if (show) {
      setMode(T.isEdu ? 'student' : 'enrollment');
      setEnrollmentId(''); setStudentRecordId(''); setTemplateId(''); setGrade(''); setForce(false);
      setManual({ studentName: '', studentEmail: '', courseName: '', batchNumber: '', completionDate: '', grade: '', issuerName: '', issuerTitle: '', certificateTitle: '', subtitle: '' });
      setExtraIssueFields([]);
      setHiddenTemplateFields([]);
      setNewField({ label: '', key: '', type: 'text', maxStars: 5 });
    }
  }, [show, T.isEdu]);

  // Lazy-load student records once when the student tab opens.
  useEffect(() => {
    if (!show || mode !== 'student' || students.length > 0) return;
    setStudentLoading(true);
    api.get('/records/student_management', { params: { limit: 500 } })
      .then(r => setStudents(r.data?.records || []))
      .catch(() => setStudents([]))
      .finally(() => setStudentLoading(false));
  }, [show, mode, students.length]);

  const filteredStudents = (() => {
    const q = studentSearch.trim().toLowerCase();
    if (!q) return students;
    return students.filter(s => {
      const d = s.recordData || {};
      const name = `${d.first_name || d.firstName || ''} ${d.last_name || d.lastName || ''}`.toLowerCase();
      const adm  = String(d.admission_number || '').toLowerCase();
      const cls  = String(d.class_grade || '').toLowerCase();
      return name.includes(q) || adm.includes(q) || cls.includes(q);
    });
  })();

  // When the user picks a student, auto-fill the manual form so the issuance
  // payload carries the right name/class/section without retyping.
  const onPickStudent = (id) => {
    setStudentRecordId(id);
    const stu = students.find(s => s._id === id);
    if (!stu) return;
    const d = stu.recordData || {};
    setManual(m => ({
      ...m,
      studentName:  `${d.first_name || d.firstName || ''} ${d.last_name || d.lastName || ''}`.trim() || d.full_name || d.name || '',
      studentEmail: d.email || d.student_email || '',
      batchNumber:  [d.class_grade, d.section].filter(Boolean).join(' — '),
    }));
  };

  const updateManual = (k, v) => setManual(m => ({ ...m, [k]: v }));
  const selectedTemplate = useMemo(() => {
    if (templateId) return templates.find(t => t._id === templateId) || null;
    return templates.find(t => t.isDefault) || templates[0] || null;
  }, [templateId, templates]);
  const customTemplateFields = useMemo(() => {
    const raw = selectedTemplate?.fields || [];
    const seen = new Set();
    return raw.filter((f) => {
      if (!f || f.isStatic || !f.key || f.type === 'mask') return false;
      if (BUILTIN_CERT_KEYS.has(f.key)) return false;
      if (seen.has(f.key)) return false;
      seen.add(f.key);
      return true;
    });
  }, [selectedTemplate]);
  const visibleCustomFields = useMemo(() => {
    const templateFields = customTemplateFields.filter(f => !hiddenTemplateFields.includes(f.key));
    const merged = [...templateFields];
    extraIssueFields.forEach((f) => {
      if (!merged.some(x => x.key === f.key)) merged.push(f);
    });
    return merged;
  }, [customTemplateFields, hiddenTemplateFields, extraIssueFields]);

  const removeIssueField = (field) => {
    if (!field?.key) return;
    if (extraIssueFields.some(f => f.key === field.key)) {
      setExtraIssueFields(list => list.filter(f => f.key !== field.key));
    } else {
      setHiddenTemplateFields(list => list.includes(field.key) ? list : [...list, field.key]);
    }
    updateManual(field.key, '');
  };

  const restoreTemplateFields = () => {
    setHiddenTemplateFields([]);
  };

  const addIssueField = () => {
    const label = (newField.label || '').trim();
    const key = (newField.key || '').trim().replace(/[^a-zA-Z0-9_]/g, '_');
    if (!label) return onError('Field label is required');
    if (!key) return onError('Field key is required');
    if (BUILTIN_CERT_KEYS.has(key) || visibleCustomFields.some(f => f.key === key)) {
      return onError('Field key already exists');
    }
    setExtraIssueFields(list => [...list, {
      key,
      label,
      type: newField.type || 'text',
      maxStars: Number(newField.maxStars || 5),
    }]);
    setNewField({ label: '', key: '', type: 'text', maxStars: 5 });
  };

  const handleSubmit = async () => {
    setBusy(true);
    try {
      if (mode === 'enrollment') {
        if (!enrollmentId) { setBusy(false); return onError('Select an enrollment'); }
        const extraPayload = Object.fromEntries(visibleCustomFields.map(f => [f.key, manual[f.key] || '']));
        await postData('/training/certificates/issue', {
          enrollmentId,
          templateId: templateId || undefined,
          extras: {
            grade,
            certificateTitle: manual.certificateTitle || '',
            subtitle: manual.subtitle || '',
            issuerName: manual.issuerName || '',
            issuerTitle: manual.issuerTitle || '',
            ...extraPayload,
          },
          force,
        });
      } else if (mode === 'student') {
        if (!studentRecordId)              { setBusy(false); return onError('Select a student'); }
        if (!manual.courseName.trim())     { setBusy(false); return onError(`${T.courseName} is required`); }
        if (!manual.studentName.trim())    { setBusy(false); return onError('Student name missing — re-pick the student'); }
        // Use the manual-entry path on the backend (no enrollment exists),
        // but include studentRecordId so the certificate links back to the
        // student record for follow-up reports.
        await postData('/training/certificates/issue', {
          templateId: templateId || undefined,
          manual: { ...manual, studentRecordId },
        });
      } else {
        if (!manual.studentName.trim()) { setBusy(false); return onError('Student name is required'); }
        if (!manual.courseName.trim())  { setBusy(false); return onError(`${T.courseName} is required`); }
        await postData('/training/certificates/issue', {
          templateId: templateId || undefined,
          manual,
        });
      }
      onIssued();
    } catch (e) {
      onError(e.response?.data?.message || 'Issue failed');
    } finally { setBusy(false); }
  };

  return (
    <BSModal show={show} onClose={onClose} title="Issue Certificate" size="modal-lg"
      footer={<>
        <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={handleSubmit} disabled={busy}>
          {busy ? 'Issuing...' : 'Issue Certificate'}
        </button>
      </>}>
      {/* Mode toggle — education tenants get "Pick Student" instead of
          "From Completed Enrollment" since they don't have enrollments. */}
      <ul className="nav nav-pills mb-3">
        {T.isEdu ? (
          <li className="nav-item">
            <button className={`nav-link ${mode === 'student' ? 'active' : ''}`} onClick={() => setMode('student')}>
              <i className="bi bi-person-badge me-1" />Pick Student
            </button>
          </li>
        ) : (
          <li className="nav-item">
            <button className={`nav-link ${mode === 'enrollment' ? 'active' : ''}`} onClick={() => setMode('enrollment')}>
              <i className="bi bi-link-45deg me-1" />From Completed Enrollment
            </button>
          </li>
        )}
        <li className="nav-item ms-2">
          <button className={`nav-link ${mode === 'manual' ? 'active' : ''}`} onClick={() => setMode('manual')}>
            <i className="bi bi-pencil-square me-1" />Manual Entry
          </button>
        </li>
      </ul>

      {/* Template picker — shared by both modes */}
      <div className="mb-3">
        <label className="form-label">Template (optional — falls back to default)</label>
        <select className="form-select" value={templateId} onChange={e => setTemplateId(e.target.value)}>
          <option value="">Default</option>
          {templates.map(t => <option key={t._id} value={t._id}>{t.name} v{t.version}{t.isDefault ? ' (default)' : ''}</option>)}
        </select>
        {selectedTemplate && (
          <div className="small text-muted mt-1">
            {selectedTemplate.name} · {selectedTemplate.backgroundType} · {selectedTemplate.orientation}
          </div>
        )}
      </div>

      {mode === 'student' ? (
        <>
          <div className="mb-3">
            <label className="form-label">Student <span className="text-muted small">(from Student Management)</span></label>
            <input className="form-control form-control-sm mb-2"
              placeholder="Search by name, admission no, or class..."
              value={studentSearch}
              onChange={e => setStudentSearch(e.target.value)} />
            {studentLoading ? (
              <div className="text-muted small"><span className="spinner-border spinner-border-sm me-2"/>Loading students…</div>
            ) : students.length === 0 ? (
              <div className="alert alert-warning py-2 small mb-0">
                <i className="bi bi-exclamation-triangle me-1"/>
                No student records found. Add students in the <strong>Students</strong> module first, or use Manual Entry.
              </div>
            ) : (
              <select className="form-select" size={6} value={studentRecordId}
                onChange={e => onPickStudent(e.target.value)}>
                {filteredStudents.length === 0 && <option disabled>No students match "{studentSearch}"</option>}
                {filteredStudents.map(s => {
                  const d = s.recordData || {};
                  const name = `${d.first_name || d.firstName || ''} ${d.last_name || d.lastName || ''}`.trim() || d.full_name || '(unnamed)';
                  const adm  = d.admission_number ? ` · ${d.admission_number}` : '';
                  const cls  = [d.class_grade, d.section].filter(Boolean).join(' — ');
                  return (
                    <option key={s._id} value={s._id}>
                      {name}{adm}{cls ? ` · ${cls}` : ''}
                    </option>
                  );
                })}
              </select>
            )}
          </div>

          {studentRecordId && (
            <>
              <div className="alert alert-info small py-2">
                <i className="bi bi-info-circle me-1"/>
                Pre-filled from the student record. Add the {T.courseLower} below.
              </div>
              <div className="row g-2">
                <div className="col-md-6">
                  <label className="form-label small">Student Name</label>
                  <input className="form-control" value={manual.studentName} disabled />
                </div>
                <div className="col-md-6">
                  <label className="form-label small">{T.batchNumber}</label>
                  <input className="form-control" value={manual.batchNumber}
                    onChange={e => updateManual('batchNumber', e.target.value)} />
                </div>
                <div className="col-md-8">
                  <label className="form-label small">{T.courseName} <span className="text-danger">*</span></label>
                  <input className="form-control" value={manual.courseName}
                    onChange={e => updateManual('courseName', e.target.value)}
                    placeholder={T.isEdu ? 'e.g. Class 10 Annual Promotion' : 'e.g. Full-Stack Web Development'} />
                </div>
                <div className="col-md-4">
                  <label className="form-label small">Completion Date</label>
                  <input className="form-control" type="date" value={manual.completionDate}
                    onChange={e => updateManual('completionDate', e.target.value)} />
                </div>
                <div className="col-md-6">
                  <label className="form-label small">Grade / Score</label>
                  <input className="form-control" value={manual.grade}
                    onChange={e => updateManual('grade', e.target.value)} placeholder="optional, e.g. A+" />
                </div>
                <div className="col-md-6">
                  <label className="form-label small">Student Email</label>
                  <input className="form-control" type="email" value={manual.studentEmail}
                    onChange={e => updateManual('studentEmail', e.target.value)}
                    placeholder="for emailing the certificate" />
                </div>
                <div className="col-md-6">
                  <label className="form-label small">Issuer Name</label>
                  <input className="form-control" value={manual.issuerName}
                    onChange={e => updateManual('issuerName', e.target.value)}
                    placeholder="optional" />
                </div>
                <div className="col-md-6">
                  <label className="form-label small">Issuer Title</label>
                  <input className="form-control" value={manual.issuerTitle}
                    onChange={e => updateManual('issuerTitle', e.target.value)}
                    placeholder="optional" />
                </div>
                {customTemplateFields.map((field) => (
                  <div className="col-md-6" key={`student-${field.key}`}>
                    <label className="form-label small">{field.label || field.key}</label>
                    <input
                      className="form-control"
                      type={field.type === 'stars' ? 'number' : 'text'}
                      min={field.type === 'stars' ? '0' : undefined}
                      max={field.type === 'stars' ? String(field.maxStars || 5) : undefined}
                      value={manual[field.key] || ''}
                      onChange={e => updateManual(field.key, e.target.value)}
                      placeholder={field.type === 'stars' ? `0-${field.maxStars || 5}` : 'optional'}
                    />
                  </div>
                ))}
              </div>
              <IssueCustomFieldsEditor
                fields={visibleCustomFields}
                values={manual}
                onChange={updateManual}
                onRemove={removeIssueField}
                onRestore={restoreTemplateFields}
                hiddenCount={hiddenTemplateFields.length}
                newField={newField}
                setNewField={setNewField}
                onAdd={addIssueField}
              />
            </>
          )}
        </>
      ) : mode === 'enrollment' ? (
        <>
          <div className="mb-3">
            <label className="form-label">
              Enrollment <span className="text-muted small">(active, ongoing, or completed — cancelled/discontinued are hidden)</span>
            </label>
            <select className="form-select" value={enrollmentId} onChange={e => setEnrollmentId(e.target.value)}>
              <option value="">Select...</option>
              {enrollments.map(e => (
                <option key={e._id} value={e._id}>
                  {e.enrollmentNumber} — {e.student?.firstName} {e.student?.lastName} — {e.course?.courseName}
                  {e.status && e.status !== 'completed' ? `  [${e.status}]` : ''}
                </option>
              ))}
            </select>
            {!enrollments.length && (
              <div className="small text-muted mt-1">
                No enrollments found. Create one in the <strong>Enrollments</strong> page first, or use the <strong>Manual</strong> tab above.
              </div>
            )}
          </div>
          <div className="mb-3">
            <label className="form-label">Grade / Score <span className="text-muted small">(optional)</span></label>
            <input className="form-control" value={grade} onChange={e => setGrade(e.target.value)} placeholder="e.g. A+, 92%" />
          </div>
          <div className="row g-2 mb-3">
            <div className="col-md-6">
              <label className="form-label small">Certificate Title</label>
              <input className="form-control" value={manual.certificateTitle}
                     onChange={e => updateManual('certificateTitle', e.target.value)}
                     placeholder="optional" />
            </div>
            <div className="col-md-6">
              <label className="form-label small">Subtitle</label>
              <input className="form-control" value={manual.subtitle}
                     onChange={e => updateManual('subtitle', e.target.value)}
                     placeholder="optional" />
            </div>
            <div className="col-md-6">
              <label className="form-label small">Issuer Name</label>
              <input className="form-control" value={manual.issuerName}
                     onChange={e => updateManual('issuerName', e.target.value)}
                     placeholder="optional" />
            </div>
            <div className="col-md-6">
              <label className="form-label small">Issuer Title</label>
              <input className="form-control" value={manual.issuerTitle}
                     onChange={e => updateManual('issuerTitle', e.target.value)}
                     placeholder="optional" />
            </div>
          </div>
          <IssueCustomFieldsEditor
            fields={visibleCustomFields}
            values={manual}
            onChange={updateManual}
            onRemove={removeIssueField}
            onRestore={restoreTemplateFields}
            hiddenCount={hiddenTemplateFields.length}
            newField={newField}
            setNewField={setNewField}
            onAdd={addIssueField}
          />
          <div className="form-check">
            <input className="form-check-input" type="checkbox" id="cert-force-reissue"
                   checked={force} onChange={e => setForce(e.target.checked)} />
            <label className="form-check-label small" htmlFor="cert-force-reissue">
              <strong>Force re-issue</strong> — regenerate even if a certificate already exists for this enrollment
              (useful after editing the template)
            </label>
          </div>
        </>
      ) : (
        <>
          <div className="alert alert-info small py-2">
            {T.manualHint}
          </div>
          <div className="row g-2">
            <div className="col-md-6">
              <label className="form-label small">Student Name <span className="text-danger">*</span></label>
              <input className="form-control" value={manual.studentName}
                     onChange={e => updateManual('studentName', e.target.value)}
                     placeholder="e.g. Priya Sharma" />
            </div>
            <div className="col-md-6">
              <label className="form-label small">Student Email <span className="text-muted">(for emailing cert)</span></label>
              <input className="form-control" type="email" value={manual.studentEmail}
                     onChange={e => updateManual('studentEmail', e.target.value)}
                     placeholder="student@example.com" />
            </div>
            <div className="col-md-8">
              <label className="form-label small">{T.courseName} <span className="text-danger">*</span></label>
              <input className="form-control" value={manual.courseName}
                     onChange={e => updateManual('courseName', e.target.value)}
                     placeholder={T.isEdu ? 'e.g. Class 10 Annual Promotion' : 'e.g. Full-Stack Web Development'} />
            </div>
            <div className="col-md-4">
              <label className="form-label small">{T.batchNumber}</label>
              <input className="form-control" value={manual.batchNumber}
                     onChange={e => updateManual('batchNumber', e.target.value)}
                     placeholder={T.isEdu ? 'e.g. Class 10-A' : 'optional'} />
            </div>
            <div className="col-md-6">
              <label className="form-label small">Completion Date</label>
              <input className="form-control" type="date" value={manual.completionDate}
                     onChange={e => updateManual('completionDate', e.target.value)} />
              <div className="form-text small">Defaults to today if left empty.</div>
            </div>
            <div className="col-md-6">
              <label className="form-label small">Grade / Score</label>
              <input className="form-control" value={manual.grade}
                     onChange={e => updateManual('grade', e.target.value)}
                     placeholder="optional, e.g. A+" />
            </div>
            <div className="col-md-6">
              <label className="form-label small">Certificate Title</label>
              <input className="form-control" value={manual.certificateTitle}
                     onChange={e => updateManual('certificateTitle', e.target.value)}
                     placeholder="optional, e.g. Certificate of Completion" />
            </div>
            <div className="col-md-6">
              <label className="form-label small">Subtitle</label>
              <input className="form-control" value={manual.subtitle}
                     onChange={e => updateManual('subtitle', e.target.value)}
                     placeholder="optional" />
            </div>
            <div className="col-md-6">
              <label className="form-label small">Issuer Name</label>
              <input className="form-control" value={manual.issuerName}
                     onChange={e => updateManual('issuerName', e.target.value)}
                     placeholder="optional" />
            </div>
            <div className="col-md-6">
              <label className="form-label small">Issuer Title</label>
              <input className="form-control" value={manual.issuerTitle}
                     onChange={e => updateManual('issuerTitle', e.target.value)}
                     placeholder="optional, e.g. Director" />
            </div>
          </div>
          <IssueCustomFieldsEditor
            fields={visibleCustomFields}
            values={manual}
            onChange={updateManual}
            onRemove={removeIssueField}
            onRestore={restoreTemplateFields}
            hiddenCount={hiddenTemplateFields.length}
            newField={newField}
            setNewField={setNewField}
            onAdd={addIssueField}
          />
        </>
      )}
    </BSModal>
  );
}

function IssueCustomFieldsEditor({ fields, values, onChange, onRemove, onRestore, hiddenCount, newField, setNewField, onAdd }) {
  return (
    <div className="card border-0 bg-light mt-3">
      <div className="card-body p-3">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div className="fw-semibold">Custom Fields</div>
          {hiddenCount > 0 && (
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onRestore}>
              <i className="bi bi-arrow-counterclockwise me-1" />Restore Removed
            </button>
          )}
        </div>
        {fields.length > 0 ? (
          <div className="row g-2 mb-3">
            {fields.map((field) => (
              <div className="col-md-6" key={field.key}>
                <label className="form-label small d-flex justify-content-between align-items-center">
                  <span>{field.label || field.key}</span>
                  <button type="button" className="btn btn-link btn-sm text-danger p-0" onClick={() => onRemove(field)}>
                    Remove
                  </button>
                </label>
                <input
                  className="form-control"
                  type={field.type === 'stars' ? 'number' : 'text'}
                  min={field.type === 'stars' ? '0' : undefined}
                  max={field.type === 'stars' ? String(field.maxStars || 5) : undefined}
                  value={values[field.key] || ''}
                  onChange={e => onChange(field.key, e.target.value)}
                  placeholder={field.type === 'stars' ? `0-${field.maxStars || 5}` : 'optional'}
                />
              </div>
            ))}
          </div>
        ) : (
          <div className="small text-muted mb-3">No custom fields selected for this certificate.</div>
        )}
        <div className="row g-2 align-items-end">
          <div className="col-md-4">
            <label className="form-label small">Field Label</label>
            <input
              className="form-control form-control-sm"
              value={newField.label}
              onChange={e => setNewField(f => ({ ...f, label: e.target.value }))}
              placeholder="e.g. Internship Duration"
            />
          </div>
          <div className="col-md-3">
            <label className="form-label small">Field Key</label>
            <input
              className="form-control form-control-sm"
              value={newField.key}
              onChange={e => setNewField(f => ({ ...f, key: e.target.value.replace(/[^a-zA-Z0-9_]/g, '_') }))}
              placeholder="internship_duration"
            />
          </div>
          <div className="col-md-2">
            <label className="form-label small">Type</label>
            <select className="form-select form-select-sm" value={newField.type} onChange={e => setNewField(f => ({ ...f, type: e.target.value }))}>
              <option value="text">Text</option>
              <option value="stars">Stars</option>
            </select>
          </div>
          <div className="col-md-2">
            <label className="form-label small">Max</label>
            <input
              className="form-control form-control-sm"
              type="number"
              min="1"
              max="10"
              value={newField.maxStars}
              onChange={e => setNewField(f => ({ ...f, maxStars: e.target.value }))}
              disabled={newField.type !== 'stars'}
            />
          </div>
          <div className="col-md-1">
            <button type="button" className="btn btn-sm btn-primary w-100" onClick={onAdd}>
              <i className="bi bi-plus-lg" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function EditCertificateRecordDialog({ show, record, templates, onClose, onSaved, onError }) {
  const [templateId, setTemplateId] = useState('');
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [extraIssueFields, setExtraIssueFields] = useState([]);
  const [hiddenTemplateFields, setHiddenTemplateFields] = useState([]);
  const [newField, setNewField] = useState({ label: '', key: '', type: 'text', maxStars: 5 });

  useEffect(() => {
    if (!show || !record) return;
    setTemplateId(record.templateId?._id || record.templateId || '');
    setForm({
      ...(record.data || {}),
      completionDate: toInputDate(record.data?.completionDate),
    });
    setExtraIssueFields([]);
    setHiddenTemplateFields([]);
    setNewField({ label: '', key: '', type: 'text', maxStars: 5 });
    setBusy(false);
  }, [show, record]);

  if (!record) return null;

  const selectedTemplate = (() => {
    if (templateId) return templates.find(t => t._id === templateId) || null;
    return templates.find(t => t._id === (record.templateId?._id || record.templateId)) || null;
  })();

  const customTemplateFields = (() => {
    const raw = selectedTemplate?.fields || [];
    const seen = new Set();
    return raw.filter((field) => {
      if (!field || field.isStatic || !field.key || field.type === 'mask') return false;
      if (BUILTIN_CERT_KEYS.has(field.key)) return false;
      if (seen.has(field.key)) return false;
      seen.add(field.key);
      return true;
    });
  })();

  const extraDataFields = (() => {
    const currentKeys = Object.keys(form || {}).filter((key) => (
      !BUILTIN_CERT_KEYS.has(key)
      && !customTemplateFields.some(field => field.key === key)
      && !['verifyUrl', 'qrDataUrl', 'certificateNumber', 'courseCode', 'tenantName', 'tenantLogoUrl', 'enrollmentNumber'].includes(key)
    ));
    return currentKeys.map((key) => ({
      key,
      label: key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
      type: 'text',
      maxStars: 5,
    }));
  })();

  const visibleCustomFields = (() => {
    const baseFields = [...customTemplateFields, ...extraDataFields].filter(f => !hiddenTemplateFields.includes(f.key));
    const merged = [...baseFields];
    extraIssueFields.forEach((field) => {
      if (!merged.some(x => x.key === field.key)) merged.push(field);
    });
    return merged;
  })();

  const updateForm = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const removeIssueField = (field) => {
    if (!field?.key) return;
    if (extraIssueFields.some(f => f.key === field.key)) {
      setExtraIssueFields(list => list.filter(f => f.key !== field.key));
    } else {
      setHiddenTemplateFields(list => list.includes(field.key) ? list : [...list, field.key]);
    }
    updateForm(field.key, '');
  };

  const addIssueField = () => {
    const label = (newField.label || '').trim();
    const key = (newField.key || '').trim().replace(/[^a-zA-Z0-9_]/g, '_');
    if (!label) return onError('Field label is required');
    if (!key) return onError('Field key is required');
    if (BUILTIN_CERT_KEYS.has(key) || visibleCustomFields.some(f => f.key === key)) {
      return onError('Field key already exists');
    }
    setExtraIssueFields(list => [...list, {
      key,
      label,
      type: newField.type || 'text',
      maxStars: Number(newField.maxStars || 5),
    }]);
    setNewField({ label: '', key: '', type: 'text', maxStars: 5 });
  };

  const handleSubmit = async () => {
    setBusy(true);
    try {
      const payload = {
        studentName: form.studentName || '',
        studentEmail: form.studentEmail || '',
        courseName: form.courseName || '',
        batchNumber: form.batchNumber || '',
        completionDate: form.completionDate || '',
        grade: form.grade || '',
        certificateTitle: form.certificateTitle || '',
        subtitle: form.subtitle || '',
        issuerName: form.issuerName || '',
        issuerTitle: form.issuerTitle || '',
      };
      visibleCustomFields.forEach((field) => {
        payload[field.key] = form[field.key] || '';
      });
      await putData(`/training/certificates/${record._id}`, {
        templateId: templateId || undefined,
        data: payload,
      });
      onSaved();
    } catch (e) {
      onError(e.response?.data?.message || 'Update failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <BSModal
      show={show}
      onClose={onClose}
      title={`Edit Certificate - ${record.certificateNumber}`}
      size="modal-lg"
      footer={<>
        <button className="btn btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
        <button className="btn btn-primary" onClick={handleSubmit} disabled={busy}>
          {busy ? 'Saving...' : 'Save Changes'}
        </button>
      </>}
    >
      <div className="row g-2">
        <div className="col-md-6">
          <label className="form-label small">Certificate Number</label>
          <input className="form-control" value={record.certificateNumber} disabled />
        </div>
        <div className="col-md-6">
          <label className="form-label small">Template</label>
          <select className="form-select" value={templateId} onChange={e => setTemplateId(e.target.value)}>
            <option value="">Current Template</option>
            {templates.map(t => <option key={t._id} value={t._id}>{t.name} v{t.version}{t.isDefault ? ' (default)' : ''}</option>)}
          </select>
        </div>
        <div className="col-md-6">
          <label className="form-label small">Student Name</label>
          <input className="form-control" value={form.studentName || ''} onChange={e => updateForm('studentName', e.target.value)} />
        </div>
        <div className="col-md-6">
          <label className="form-label small">Student Email</label>
          <input className="form-control" value={form.studentEmail || ''} onChange={e => updateForm('studentEmail', e.target.value)} />
        </div>
        <div className="col-md-6">
          <label className="form-label small">Course Name</label>
          <input className="form-control" value={form.courseName || ''} onChange={e => updateForm('courseName', e.target.value)} />
        </div>
        <div className="col-md-6">
          <label className="form-label small">Batch Number</label>
          <input className="form-control" value={form.batchNumber || ''} onChange={e => updateForm('batchNumber', e.target.value)} />
        </div>
        <div className="col-md-6">
          <label className="form-label small">Completion Date</label>
          <input className="form-control" type="date" value={form.completionDate || ''} onChange={e => updateForm('completionDate', e.target.value)} />
        </div>
        <div className="col-md-6">
          <label className="form-label small">Grade / Score</label>
          <input className="form-control" value={form.grade || ''} onChange={e => updateForm('grade', e.target.value)} />
        </div>
        <div className="col-md-6">
          <label className="form-label small">Certificate Title</label>
          <input className="form-control" value={form.certificateTitle || ''} onChange={e => updateForm('certificateTitle', e.target.value)} />
        </div>
        <div className="col-md-6">
          <label className="form-label small">Subtitle</label>
          <input className="form-control" value={form.subtitle || ''} onChange={e => updateForm('subtitle', e.target.value)} />
        </div>
        <div className="col-md-6">
          <label className="form-label small">Issuer Name</label>
          <input className="form-control" value={form.issuerName || ''} onChange={e => updateForm('issuerName', e.target.value)} />
        </div>
        <div className="col-md-6">
          <label className="form-label small">Issuer Title</label>
          <input className="form-control" value={form.issuerTitle || ''} onChange={e => updateForm('issuerTitle', e.target.value)} />
        </div>
      </div>
      <IssueCustomFieldsEditor
        fields={visibleCustomFields}
        values={form}
        onChange={updateForm}
        onRemove={removeIssueField}
        onRestore={() => setHiddenTemplateFields([])}
        hiddenCount={hiddenTemplateFields.length}
        newField={newField}
        setNewField={setNewField}
        onAdd={addIssueField}
      />
    </BSModal>
  );
}

// ─── Bulk issue dialog ────────────────────────────────────────────────────────

function CertificateDesignEditorDialog({ show, record, onClose, onSaved, onError }) {
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState(null);
  const [mode, setMode] = useState('image');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [selectedFieldId, setSelectedFieldId] = useState(null);
  const [dragging, setDragging] = useState(null);
  const [resizing, setResizing] = useState(null);
  const [rotating, setRotating] = useState(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!show || !record?._id) {
      setDetail(null);
      setForm(null);
      setSelectedFieldId(null);
      return;
    }
    let mounted = true;
    const load = async () => {
      setLoading(true);
      setDetail(null);
      setForm(null);
      setSelectedFieldId(null);
      try {
        const certRes = await getData(`/training/certificates/${record._id}`);
        const cert = certRes?.data || certRes;
        let template = cert.templateSnapshot || null;
        if (!template) {
          const templateId = cert.templateId?._id || cert.templateId;
          if (templateId) {
            try {
              const templateRes = await getData(`/training/certificates/templates/${templateId}`);
              template = templateRes?.data || templateRes;
            } catch (_) {
              template = null;
            }
          }
        }
        if (!template) {
          try {
            const templatesRes = await getData('/training/certificates/templates');
            const list = templatesRes?.records || [];
            template = list.find(t => t.isDefault) || list[0] || null;
          } catch (_) {
            template = null;
          }
        }
        if (!template) {
          template = buildCertificateEditorFallbackTemplate();
        }
        if (!mounted) return;
        const normalizedTemplate = {
          ...template,
          fields: ((template?.fields && template.fields.length) ? template.fields : ((template?.backgroundType || 'image') === 'image' ? buildDefaultCertificateImageFields() : [])).map((field, idx) => ({
            ...field,
            _id: field._id || `field-${idx}-${Date.now()}`,
          })),
        };
        setDetail(cert);
        setForm(normalizedTemplate);
        setMode(normalizedTemplate.backgroundType === 'image' ? 'image' : 'html');
        setSelectedFieldId(null);
        setPreviewUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return null;
        });
      } catch (e) {
        if (mounted) onError(e.response?.data?.message || 'Failed to load certificate design');
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, [show, record?._id]);

  useEffect(() => () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  useEffect(() => {
    if (!dragging) return;
    const onMove = (e) => {
      const dx = e.clientX - dragging.startX;
      const dy = e.clientY - dragging.startY;
      const pctX = (dx / dragging.rectW) * 100;
      const pctY = (dy / dragging.rectH) * 100;
      setForm(f => f ? ({
        ...f,
        fields: f.fields.map(fd => fd._id === dragging.id ? {
          ...fd,
          x: Math.max(0, Math.min(100, dragging.origX + pctX)),
          y: Math.max(0, Math.min(100, dragging.origY + pctY)),
        } : fd),
      }) : f);
    };
    const onUp = () => setDragging(null);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [dragging]);

  useEffect(() => {
    if (!resizing) return;
    const onMove = (e) => {
      const dx = e.clientX - resizing.startX;
      const dy = e.clientY - resizing.startY;
      const pctW = (dx / resizing.rectW) * 100;
      const pctH = (dy / resizing.rectH) * 100;
      setForm(f => f ? ({
        ...f,
        fields: f.fields.map(fd => fd._id === resizing.id ? {
          ...fd,
          w: Math.max(4, Math.min(100 - fd.x, resizing.origW + pctW)),
          h: Math.max(3, Math.min(100 - fd.y, resizing.origH + pctH)),
        } : fd),
      }) : f);
    };
    const onUp = () => setResizing(null);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [resizing]);

  useEffect(() => {
    if (!rotating) return;
    const onMove = (e) => {
      const angle = Math.atan2(e.clientY - rotating.centerY, e.clientX - rotating.centerX) * (180 / Math.PI);
      setForm(f => f ? ({
        ...f,
        fields: f.fields.map(fd => fd._id === rotating.id ? {
          ...fd,
          rotation: Math.round(angle),
        } : fd),
      }) : f);
    };
    const onUp = () => setRotating(null);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [rotating]);

  const update = (patch) => setForm(f => ({ ...f, ...patch }));
  const updateField = (id, patch) => setForm(f => ({ ...f, fields: (f.fields || []).map(fd => fd._id === id ? { ...fd, ...patch } : fd) }));
  const removeField = (id) => {
    setForm(f => ({ ...f, fields: (f.fields || []).filter(fd => fd._id !== id) }));
    if (selectedFieldId === id) setSelectedFieldId(null);
  };

  const addField = (key, overrideLabel) => {
    const label = overrideLabel || FIELD_KEYS.find(k => k.key === key)?.label || key;
    setForm(f => ({
      ...f,
      fields: [...(f.fields || []), {
        _id: `new-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        type: 'text',
        key, label, x: 20, y: 45, w: 60, h: 10,
        fontSize: 24, fontFamily: 'Georgia, serif',
        fontWeight: 'bold', fontStyle: 'normal', color: '#1a1a1a', align: 'center', lineHeight: 1.2, letterSpacing: 0, rotation: 0,
        backgroundColor: 'transparent', borderRadius: 0, opacity: 1, zIndex: 2, srcKey: '', maxStars: 5, isStatic: false,
      }],
    }));
  };

  const addElement = (type) => {
    const stamp = `new-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    setForm(f => ({
      ...f,
      fields: [...(f.fields || []), {
        _id: stamp,
        type,
        key: type === 'mask' ? `mask_${stamp.slice(-4)}` : type === 'image' ? 'tenantLogoUrl' : 'grade',
        label: type === 'mask' ? 'Mask Box' : type === 'image' ? 'Image Element' : 'Stars',
        x: 20, y: 20, w: type === 'mask' ? 20 : 28, h: type === 'mask' ? 10 : 8,
        fontSize: 24, fontFamily: 'Georgia, serif', fontWeight: 'bold', fontStyle: 'normal',
        color: type === 'stars' ? '#f59e0b' : '#1a1a1a', align: 'center', lineHeight: 1.2, letterSpacing: 0, rotation: 0,
        backgroundColor: type === 'mask' ? '#ffffff' : 'transparent', borderRadius: type === 'mask' ? 4 : 0, opacity: 1, zIndex: 2,
        srcKey: type === 'image' ? 'tenantLogoUrl' : '', maxStars: 5, isStatic: false,
      }],
    }));
  };

  const addCustomField = () => {
    const label = window.prompt('Field label');
    if (!label) return;
    const rawKey = window.prompt('Field key (letters/numbers/underscore only)');
    if (!rawKey) return;
    const key = rawKey.trim().replace(/[^a-zA-Z0-9_]/g, '_');
    if (!key) return;
    addField(key, label.trim());
  };

  const handleMouseDown = (e, field) => {
    e.preventDefault();
    const rect = canvasRef.current.getBoundingClientRect();
    setSelectedFieldId(field._id);
    setDragging({
      id: field._id,
      startX: e.clientX,
      startY: e.clientY,
      origX: field.x,
      origY: field.y,
      rectW: rect.width,
      rectH: rect.height,
    });
  };

  const handleResizeMouseDown = (e, field) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = canvasRef.current.getBoundingClientRect();
    setSelectedFieldId(field._id);
    setResizing({
      id: field._id,
      startX: e.clientX,
      startY: e.clientY,
      origW: field.w,
      origH: field.h,
      rectW: rect.width,
      rectH: rect.height,
    });
  };

  const handleRotateMouseDown = (e, field) => {
    e.preventDefault();
    e.stopPropagation();
    const canvasRect = canvasRef.current.getBoundingClientRect();
    const centerX = canvasRect.left + ((field.x + (field.w / 2)) / 100) * canvasRect.width;
    const centerY = canvasRect.top + ((field.y + (field.h / 2)) / 100) * canvasRect.height;
    setSelectedFieldId(field._id);
    setRotating({ id: field._id, centerX, centerY });
  };

  const renderFieldValue = (field) => {
    if (field.type === 'mask') return '';
    if (field.type === 'image') return <span className="small text-muted">{field.srcKey || field.key || 'image'}</span>;
    if (field.type === 'stars') {
      const score = Number(resolvedRecordData?.[field.key]);
      const count = Number.isFinite(score) && score > 0 ? Math.min(field.maxStars || 5, Math.max(1, Math.round(score))) : (field.maxStars || 5);
      return '★★★★★'.slice(0, count) || '★';
    }
    if (field.isStatic) return field.label || 'Static Text';
    const value = resolvedRecordData?.[field.key];
    if (value === 0) return '0';
    if (value === false) return 'false';
    return value || field.label || field.key || '';
  };

  const handlePreview = async () => {
    if (!record?._id || !form) return;
    setPreviewing(true);
    try {
      const res = await api.post(`/training/certificates/${record._id}/design/preview`, {
        templateSnapshot: { ...form, backgroundType: mode, fields: form.fields || [] },
        data: resolvedRecordData,
      }, { responseType: 'blob' });
      setPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return URL.createObjectURL(res.data);
      });
    } catch (e) {
      onError(e.response?.data?.message || 'Preview failed');
    } finally {
      setPreviewing(false);
    }
  };

  const handleSave = async () => {
    if (!record?._id || !detail || !form) return;
    setSaving(true);
    try {
      await putData(`/training/certificates/${record._id}`, {
        templateId: detail.templateId?._id || detail.templateId,
        templateSnapshot: { ...form, backgroundType: mode, fields: form.fields || [] },
        data: resolvedRecordData,
      });
      onSaved();
    } catch (e) {
      onError(e.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handlePrintPreview = () => {
    if (!previewUrl) return;
    const frame = window.open(previewUrl, '_blank', 'noopener,noreferrer');
    if (frame) {
      frame.onload = () => {
        try { frame.print(); } catch (_) {}
      };
    }
  };

  const resolvedRecordData = useMemo(() => ({
    ...(detail?.data || {}),
    studentName: detail?.data?.studentName
      || [detail?.studentId?.firstName, detail?.studentId?.lastName].filter(Boolean).join(' ').trim()
      || '',
    studentEmail: detail?.data?.studentEmail || detail?.studentId?.email || '',
    courseName: detail?.data?.courseName || detail?.courseId?.courseName || '',
    courseCode: detail?.data?.courseCode || detail?.courseId?.courseCode || '',
    batchNumber: detail?.data?.batchNumber || detail?.batchId?.batchNumber || '',
    certificateNumber: detail?.data?.certificateNumber || detail?.certificateNumber || '',
    enrollmentNumber: detail?.data?.enrollmentNumber || '',
    completionDate: detail?.data?.completionDate || '',
    grade: detail?.data?.grade || '',
    issuerName: detail?.data?.issuerName || '',
    issuerTitle: detail?.data?.issuerTitle || '',
    certificateTitle: detail?.data?.certificateTitle || 'Certificate of Completion',
    subtitle: detail?.data?.subtitle || '',
    verifyUrl: detail?.data?.verifyUrl || '',
    qrDataUrl: detail?.data?.qrDataUrl || '',
  }), [detail]);

  const selectedField = form?.fields?.find(f => f._id === selectedFieldId);
  const bgUrl = form?.backgroundAsset
    ? `${process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000'}${form.backgroundAsset}?t=${localStorage.getItem('token')}`
    : '';

  return (
    <BSModal
      show={show}
      onClose={onClose}
      title={record ? `Certificate Design - ${record.certificateNumber}` : 'Certificate Design'}
      size="modal-xl"
      footer={<>
        <button className="btn btn-outline-secondary" onClick={handlePreview} disabled={loading || saving || previewing || !form}>
          <i className="bi bi-eye me-1" />{previewing ? 'Rendering...' : 'Preview'}
        </button>
        <button className="btn btn-outline-primary" onClick={handlePrintPreview} disabled={!previewUrl}>
          <i className="bi bi-printer me-1" />Print Preview
        </button>
        <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Close</button>
        <button className="btn btn-primary" onClick={handleSave} disabled={loading || saving || !form}>
          {saving ? 'Saving...' : 'Save Design'}
        </button>
      </>}
    >
      {loading || !form ? (
        <div className="py-5 text-center text-muted">Loading certificate design...</div>
      ) : (
        <div className="row g-3">
          <div className="col-md-5">
            <div className="card border-0 bg-light mb-3">
              <div className="card-body p-3">
                <div className="small text-muted mb-2">
                  This edits only this issued certificate. Other certificates and the master template stay unchanged.
                </div>
                <div className="row g-2">
                  <div className="col-6">
                    <label className="form-label small">Background Mode</label>
                    <select className="form-select form-select-sm" value={mode} onChange={e => setMode(e.target.value)}>
                      <option value="html">HTML</option>
                      <option value="image">Image Overlay</option>
                    </select>
                  </div>
                  <div className="col-3">
                    <label className="form-label small">Width</label>
                    <input type="number" className="form-control form-control-sm" value={form.widthPx || 1123} onChange={e => update({ widthPx: parseInt(e.target.value, 10) || 1123 })} />
                  </div>
                  <div className="col-3">
                    <label className="form-label small">Height</label>
                    <input type="number" className="form-control form-control-sm" value={form.heightPx || 794} onChange={e => update({ heightPx: parseInt(e.target.value, 10) || 794 })} />
                  </div>
                </div>
              </div>
            </div>

            {mode === 'html' ? (
              <>
                <div className="mb-3">
                  <label className="form-label small">HTML Body</label>
                  <textarea className="form-control font-monospace small" rows="10" value={form.htmlBody || ''} onChange={e => update({ htmlBody: e.target.value })} />
                </div>
                <div className="mb-3">
                  <label className="form-label small">CSS</label>
                  <textarea className="form-control font-monospace small" rows="8" value={form.cssStyles || ''} onChange={e => update({ cssStyles: e.target.value })} />
                </div>
              </>
            ) : (
              <>
                <div className="card border-0 bg-light mb-3">
                  <div className="card-body p-3">
                    <label className="form-label small">Add Elements</label>
                    <div className="d-flex gap-2 align-items-end flex-wrap">
                      <div style={{ minWidth: 220, flex: '1 1 220px' }}>
                        <select className="form-select form-select-sm" value="" onChange={e => { if (e.target.value) addField(e.target.value); }}>
                          <option value="">+ Insert dynamic field...</option>
                          {FIELD_KEYS.map(k => <option key={k.key} value={k.key}>{k.label}</option>)}
                        </select>
                      </div>
                      <button type="button" className="btn btn-sm btn-outline-secondary" onClick={addCustomField}>Custom Field</button>
                      <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => addElement('mask')}>Mask</button>
                      <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => addElement('image')}>Image</button>
                      <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => addElement('stars')}>Stars</button>
                    </div>
                  </div>
                </div>

                {selectedField && (
                  <div className="card bg-light mb-3">
                    <div className="card-body p-2">
                      <div className="small fw-bold mb-2">Editing: {selectedField.label}</div>
                      <div className="row g-2">
                        <div className="col-6">
                          <label className="form-label small mb-0">Label</label>
                          <input className="form-control form-control-sm" value={selectedField.label || ''} onChange={e => updateField(selectedField._id, { label: e.target.value })} />
                        </div>
                        <div className="col-6">
                          <label className="form-label small mb-0">Data Key</label>
                          <input className="form-control form-control-sm" value={selectedField.key || ''} onChange={e => updateField(selectedField._id, { key: e.target.value.replace(/[^a-zA-Z0-9_]/g, '_') })} disabled={selectedField.type === 'mask' || selectedField.isStatic} />
                        </div>
                        <div className="col-3">
                          <label className="form-label small mb-0">Font</label>
                          <input type="number" className="form-control form-control-sm" value={selectedField.fontSize || 24} onChange={e => updateField(selectedField._id, { fontSize: parseInt(e.target.value, 10) || 24 })} />
                        </div>
                        <div className="col-3">
                          <label className="form-label small mb-0">Rotate</label>
                          <input type="number" className="form-control form-control-sm" value={Math.round(selectedField.rotation || 0)} onChange={e => updateField(selectedField._id, { rotation: parseFloat(e.target.value) || 0 })} />
                        </div>
                        <div className="col-3">
                          <label className="form-label small mb-0">W%</label>
                          <input type="number" className="form-control form-control-sm" value={Math.round(selectedField.w || 0)} onChange={e => updateField(selectedField._id, { w: parseFloat(e.target.value) || 10 })} />
                        </div>
                        <div className="col-3">
                          <label className="form-label small mb-0">H%</label>
                          <input type="number" className="form-control form-control-sm" value={Math.round(selectedField.h || 0)} onChange={e => updateField(selectedField._id, { h: parseFloat(e.target.value) || 5 })} />
                        </div>
                        <div className="col-4">
                          <label className="form-label small mb-0">Align</label>
                          <select className="form-select form-select-sm" value={selectedField.align || 'center'} onChange={e => updateField(selectedField._id, { align: e.target.value })}>
                            <option value="left">Left</option>
                            <option value="center">Center</option>
                            <option value="right">Right</option>
                          </select>
                        </div>
                        <div className="col-4">
                          <label className="form-label small mb-0">Color</label>
                          <input type="color" className="form-control form-control-color form-control-sm" value={selectedField.color || '#1a1a1a'} onChange={e => updateField(selectedField._id, { color: e.target.value })} />
                        </div>
                        <div className="col-4">
                          <label className="form-label small mb-0">Background</label>
                          <input type="color" className="form-control form-control-color form-control-sm" value={(selectedField.backgroundColor && selectedField.backgroundColor !== 'transparent') ? selectedField.backgroundColor : '#ffffff'} onChange={e => updateField(selectedField._id, { backgroundColor: e.target.value })} />
                        </div>
                        {selectedField.type === 'image' && (
                          <div className="col-12">
                            <label className="form-label small mb-0">Image Source Key</label>
                            <input className="form-control form-control-sm" value={selectedField.srcKey || ''} onChange={e => updateField(selectedField._id, { srcKey: e.target.value.replace(/[^a-zA-Z0-9_]/g, '_') })} />
                          </div>
                        )}
                        {selectedField.type === 'stars' && (
                          <div className="col-12">
                            <label className="form-label small mb-0">Max Stars</label>
                            <input type="number" min="1" max="10" className="form-control form-control-sm" value={selectedField.maxStars || 5} onChange={e => updateField(selectedField._id, { maxStars: parseInt(e.target.value, 10) || 5 })} />
                          </div>
                        )}
                        <div className="col-12">
                          <button className="btn btn-outline-danger btn-sm w-100" onClick={() => removeField(selectedField._id)}>
                            <i className="bi bi-trash me-1" />Remove Field
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="col-md-7">
            <label className="form-label small">Canvas Preview</label>
            {mode === 'image' ? (
              <div
                ref={canvasRef}
                style={{
                  position: 'relative',
                  width: '100%',
                  aspectRatio: `${form.widthPx || 1123} / ${form.heightPx || 794}`,
                  background: bgUrl ? `url("${bgUrl}") center/cover no-repeat` : (form.backgroundColor || '#fff'),
                  border: '1px solid #ccc',
                  userSelect: 'none',
                  cursor: dragging ? 'grabbing' : 'default',
                }}
              >
                {(form.fields || []).map(field => (
                  <div
                    key={field._id}
                    onMouseDown={(e) => handleMouseDown(e, field)}
                    onClick={() => setSelectedFieldId(field._id)}
                    style={{
                      position: 'absolute',
                      left: `${field.x}%`,
                      top: `${field.y}%`,
                      width: `${field.w}%`,
                      height: `${field.h}%`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: field.align === 'left' ? 'flex-start' : field.align === 'right' ? 'flex-end' : 'center',
                      fontFamily: field.fontFamily,
                      fontSize: Math.max(10, (field.fontSize || 24) * 0.5),
                      fontWeight: field.fontWeight,
                      fontStyle: field.fontStyle || 'normal',
                      lineHeight: field.lineHeight || 1.2,
                      letterSpacing: `${field.letterSpacing || 0}px`,
                      color: field.color,
                      border: selectedFieldId === field._id ? '2px dashed #0d6efd' : '1px dashed rgba(0,0,0,0.3)',
                      background: field.type === 'mask'
                        ? (field.backgroundColor || '#ffffff')
                        : (selectedFieldId === field._id ? 'rgba(13,110,253,0.08)' : (field.backgroundColor || 'rgba(255,255,255,0.3)')),
                      cursor: 'move',
                      overflow: selectedFieldId === field._id ? 'visible' : 'hidden',
                      whiteSpace: 'pre-wrap',
                      textAlign: field.align || 'center',
                      transform: `rotate(${field.rotation || 0}deg)`,
                      borderRadius: field.borderRadius || 0,
                      opacity: field.opacity ?? 1,
                      zIndex: field.zIndex || 1,
                    }}
                  >
                    {renderFieldValue(field)}
                    {selectedFieldId === field._id && (
                      <>
                        <span
                          onMouseDown={(e) => handleResizeMouseDown(e, field)}
                          style={{ position: 'absolute', right: -6, bottom: -6, width: 12, height: 12, borderRadius: 2, background: '#0d6efd', cursor: 'nwse-resize' }}
                        />
                        <span
                          onMouseDown={(e) => handleRotateMouseDown(e, field)}
                          title="Rotate"
                          style={{ position: 'absolute', left: '50%', top: -22, transform: 'translateX(-50%)', width: 14, height: 14, borderRadius: '50%', background: '#198754', border: '2px solid #fff', boxShadow: '0 0 0 1px rgba(0,0,0,0.2)', cursor: 'grab' }}
                        />
                      </>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="border rounded p-3 bg-light small text-muted">
                HTML mode uses the saved preview renderer. Click Preview to render the current layout.
              </div>
            )}

            {previewUrl && (
              <div className="mt-3">
                <label className="form-label small">Rendered Preview</label>
                <div className="border rounded overflow-hidden bg-white">
                  <img src={previewUrl} alt="Certificate preview" className="img-fluid w-100" />
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </BSModal>
  );
}

function BulkIssueDialog({ show, onClose, batches, templates, onDone, onError }) {
  const [batchId, setBatchId] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [force, setForce] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (show) { setBatchId(''); setTemplateId(''); setForce(false); } }, [show]);

  const T = useTenantLabels();
  const handleSubmit = async () => {
    if (!batchId) return onError(T.selectBatch);
    setBusy(true);
    try {
      const res = await postData('/training/certificates/bulk-issue', { batchId, templateId: templateId || undefined, force });
      onDone(res.data || res);
    } catch (e) { onError(e.response?.data?.message || 'Bulk issue failed'); }
    finally { setBusy(false); }
  };

  return (
    <BSModal show={show} onClose={onClose} title="Bulk Issue Certificates"
      footer={<>
        <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={handleSubmit} disabled={busy}>
          {busy ? 'Processing...' : T.issueForBatch}
        </button>
      </>}>
      <div className="alert alert-info small">
        {T.completion}
      </div>
      <div className="mb-3">
        <label className="form-label">{T.batch}</label>
        <select className="form-select" value={batchId} onChange={e => setBatchId(e.target.value)}>
          <option value="">Select...</option>
          {batches.map(b => <option key={b._id} value={b._id}>{b.batchNumber} {b.status === 'completed' ? '(Completed)' : ''}</option>)}
        </select>
      </div>
      <div className="mb-3">
        <label className="form-label">Template (optional)</label>
        <select className="form-select" value={templateId} onChange={e => setTemplateId(e.target.value)}>
          <option value="">Default</option>
          {templates.map(t => <option key={t._id} value={t._id}>{t.name} v{t.version}</option>)}
        </select>
      </div>
      <div className="form-check">
        <input className="form-check-input" type="checkbox" id="force-reissue" checked={force} onChange={e => setForce(e.target.checked)} />
        <label className="form-check-label" htmlFor="force-reissue">
          Force re-issue (overwrite existing certificates)
        </label>
      </div>
    </BSModal>
  );
}

function BulkEmailDialog({ show, onClose, batches, onDone, onError }) {
  const T = useTenantLabels();
  const [batchId, setBatchId] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (show) setBatchId(''); }, [show]);
  const handleSubmit = async () => {
    if (!batchId) return onError(T.selectBatch);
    setBusy(true);
    try {
      const res = await postData('/training/certificates/bulk-email', { batchId });
      onDone(res.data || res);
    } catch (e) { onError(e.response?.data?.message || 'Bulk email failed'); }
    finally { setBusy(false); }
  };
  return (
    <BSModal show={show} onClose={onClose} title="Bulk Email Certificates"
      footer={<>
        <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn btn-info text-white" onClick={handleSubmit} disabled={busy}>
          {busy ? 'Sending...' : 'Send Emails'}
        </button>
      </>}>
      <div className="alert alert-info small">
        {T.bulkEmailHint}
      </div>
      <div className="mb-3">
        <label className="form-label">{T.batch}</label>
        <select className="form-select" value={batchId} onChange={e => setBatchId(e.target.value)}>
          <option value="">Select...</option>
          {batches.map(b => <option key={b._id} value={b._id}>{b.batchNumber}</option>)}
        </select>
      </div>
    </BSModal>
  );
}

function RevokeDialog({ record, onClose, onConfirm }) {
  const [reason, setReason] = useState('');
  useEffect(() => { if (record) setReason(''); }, [record]);
  return (
    <BSModal show={!!record} onClose={onClose} title="Revoke Certificate"
      footer={<>
        <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn btn-danger" onClick={() => onConfirm(reason)}>Revoke</button>
      </>}>
      <p>Revoking <strong>{record?.certificateNumber}</strong> will mark it as invalid on the public verification page. This is not reversible via the UI.</p>
      <div className="mb-3">
        <label className="form-label">Reason</label>
        <textarea className="form-control" rows="3" value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. issued in error, student withdrew" />
      </div>
    </BSModal>
  );
}

function RegenerateDialog({ record, templates, onClose, onDone, onError }) {
  const T = useTenantLabels();
  const [templateId, setTemplateId] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (record) { setTemplateId(''); setBusy(false); } }, [record]);

  const handleSubmit = async () => {
    setBusy(true);
    try {
      const res = await postData(`/training/certificates/${record._id}/regenerate`, {
        templateId: templateId || undefined,
      });
      const newNumber = res.data?.regenerated?.certificateNumber || res.regenerated?.certificateNumber || 'new cert';
      onDone(newNumber);
    } catch (e) {
      onError(e.response?.data?.message || 'Regenerate failed');
      setBusy(false);
    }
  };

  return (
    <BSModal show={!!record} onClose={onClose} title="Regenerate Certificate"
      footer={<>
        <button className="btn btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
        <button className="btn btn-warning" onClick={handleSubmit} disabled={busy}>
          {busy ? 'Regenerating...' : 'Revoke Old & Issue New'}
        </button>
      </>}>
      <div className="alert alert-warning small">
        <strong>What this does:</strong>
        <ol className="mb-0 ps-3 mt-1">
          <li>The old certificate <strong>{record?.certificateNumber}</strong> will be <strong>revoked</strong> (public verification will show it as invalid).</li>
          <li>A new certificate is issued with the <strong>same student data</strong> but using the current template.</li>
          <li>The new certificate gets a fresh number, QR code, and verification URL.</li>
        </ol>
      </div>
      <div className="mb-3">
        <label className="form-label small">Template <span className="text-muted">(optional — uses current default if blank)</span></label>
        <select className="form-select form-select-sm" value={templateId} onChange={e => setTemplateId(e.target.value)}>
          <option value="">Current default template</option>
          {templates.map(t => (
            <option key={t._id} value={t._id}>
              {t.name} v{t.version} {t.isDefault ? '(default)' : ''} {t.status === 'draft' ? '— draft' : ''}
            </option>
          ))}
        </select>
      </div>
      <div className="small text-muted">
        Student: <strong>{record?.data?.studentName || '—'}</strong> ·
        {T.course}: <strong>{record?.data?.courseName || '—'}</strong>
      </div>
    </BSModal>
  );
}

// ─── Templates Tab ────────────────────────────────────────────────────────────

function TemplatesTab({ canWrite, canAdmin, showAlert }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showStarter, setShowStarter] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getData('/training/certificates/templates');
      setTemplates(res.records || res.data || []);
    } catch (e) { showAlert(e.response?.data?.message || 'Load failed', 'danger'); }
    finally { setLoading(false); }
  }, [showAlert]);

  useEffect(() => { load(); }, [load]);

  const handleNew = async (starter = 'formal') => {
    try {
      const starterLabels = {
        formal: 'Formal Certificate',
        modern: 'Modern Certificate',
        image: 'Image Overlay Template',
      };
      const res = await postData('/training/certificates/templates', {
        name: starterLabels[starter] || 'New Template',
        starter,
      });
      setEditing(res.data || res);
      setShowStarter(false);
    } catch (e) { showAlert(e.response?.data?.message || 'Create failed', 'danger'); }
  };

  const handleDuplicate = async (t) => {
    try {
      const res = await postData(`/training/certificates/templates/${t._id}/duplicate`);
      load();
      setEditing(res.data || res);
    } catch (e) { showAlert(e.response?.data?.message || 'Duplicate failed', 'danger'); }
  };

  const handleSyncDefault = async (t) => {
    if (!window.confirm(
      `Sync "${t.name}" to the latest built-in design?\n\n` +
      `This refreshes the HTML/CSS to include logo, batch number, and enrollment number.\n\n` +
      `${t.status === 'published'
        ? 'A new draft version will be created (your published version stays untouched until you publish the new draft).'
        : 'Your current draft will be overwritten.'}`
    )) return;
    try {
      const res = await postData(`/training/certificates/templates/${t._id}/sync-default`);
      const updated = res.data || res;
      load();
      showAlert(t.status === 'published'
        ? 'New draft version created from defaults. Open it, preview, then publish.'
        : 'Template synced to current default.');
      setEditing(updated);
    } catch (e) { showAlert(e.response?.data?.message || 'Sync failed', 'danger'); }
  };

  const handleDelete = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"?\n\nIf certificates exist, it will be archived instead.`)) return;
    try {
      await deleteData(`/training/certificates/templates/${t._id}`);
      showAlert('Template deleted');
      load();
    } catch (e) { showAlert(e.response?.data?.message || 'Delete failed', 'danger'); }
  };

  const handleSetDefault = async (t) => {
    try {
      await putData(`/training/certificates/templates/${t._id}`, { isDefault: true });
      showAlert('Default template updated');
      load();
    } catch (e) { showAlert(e.response?.data?.message || 'Update failed', 'danger'); }
  };

  return (
    <>
      <div className="card">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div className="text-muted small">
              Templates define how certificates look. Edit a template to change its design, or duplicate an existing one.
            </div>
            {canWrite && (
              <button className="btn btn-sm btn-primary" onClick={() => setShowStarter(true)}>
                <i className="bi bi-plus-lg me-1" />New Template
              </button>
            )}
          </div>

          {loading ? (
            <div className="text-center py-5"><div className="spinner-border text-primary" /></div>
          ) : templates.length === 0 ? (
            <div className="text-center py-5 text-muted">No templates. A default will be created automatically.</div>
          ) : (
            <div className="row g-3">
              {templates.map(t => (
                <div key={t._id} className="col-md-4">
                  <div className={`card h-100 ${t.isDefault ? 'border-primary' : ''}`}>
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div>
                          <h6 className="mb-1">{t.name}</h6>
                          <div className="small text-muted">
                            v{t.version} · {t.backgroundType} · {t.orientation}
                            {t.status === 'draft' && <span className="badge bg-warning text-dark ms-2">Draft</span>}
                            {t.status === 'archived' && <span className="badge bg-secondary ms-2">Archived</span>}
                          </div>
                        </div>
                        {t.isDefault && <span className="badge bg-primary">Default</span>}
                      </div>
                      {t.description && <p className="small text-muted mb-2">{t.description}</p>}
                      <div className="btn-group btn-group-sm w-100">
                        <button className="btn btn-outline-primary" onClick={() => setEditing(t)}>
                          <i className="bi bi-pencil me-1" />Edit
                        </button>
                        {canWrite && (
                          <button className="btn btn-outline-secondary" title="Duplicate" onClick={() => handleDuplicate(t)}>
                            <i className="bi bi-copy" />
                          </button>
                        )}
                        {canWrite && t.status !== 'archived' && (
                          <button className="btn btn-outline-info" title="Sync to built-in default (logo, batch, enrollment)" onClick={() => handleSyncDefault(t)}>
                            <i className="bi bi-arrow-clockwise" />
                          </button>
                        )}
                        {canWrite && !t.isDefault && (
                          <button className="btn btn-outline-success" title="Set as default" onClick={() => handleSetDefault(t)}>
                            <i className="bi bi-star" />
                          </button>
                        )}
                        {canAdmin && !t.isDefault && (
                          <button className="btn btn-outline-danger" onClick={() => handleDelete(t)}>
                            <i className="bi bi-trash" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <TemplateEditor
        template={editing}
        onClose={() => setEditing(null)}
        onSaved={(updated) => {
          // If a new draft version was forked, keep editing that one
          if (updated && editing && updated._id !== editing._id) setEditing(updated);
          else setEditing(null);
          load();
          showAlert('Template saved');
        }}
        onError={(m) => showAlert(m, 'danger')}
      />

      <BSModal
        show={showStarter}
        onClose={() => setShowStarter(false)}
        title="Create Certificate Template"
        footer={<button className="btn btn-secondary" onClick={() => setShowStarter(false)}>Close</button>}
      >
        <div className="row g-3">
          <div className="col-md-4">
            <button className="btn btn-outline-primary w-100 h-100 text-start p-3" onClick={() => handleNew('formal')}>
              <div className="fw-semibold mb-1">Formal</div>
              <div className="small text-muted">Classic academic certificate with border and signature layout.</div>
            </button>
          </div>
          <div className="col-md-4">
            <button className="btn btn-outline-primary w-100 h-100 text-start p-3" onClick={() => handleNew('modern')}>
              <div className="fw-semibold mb-1">Modern</div>
              <div className="small text-muted">Premium clean layout for institutes, internships, and projects.</div>
            </button>
          </div>
          <div className="col-md-4">
            <button className="btn btn-outline-primary w-100 h-100 text-start p-3" onClick={() => handleNew('image')}>
              <div className="fw-semibold mb-1">From Image</div>
              <div className="small text-muted">Upload your existing certificate design and place fields over it.</div>
            </button>
          </div>
        </div>
      </BSModal>
    </>
  );
}

// ─── Template Editor ──────────────────────────────────────────────────────────

const FIELD_KEYS = [
  { key: 'studentName', label: 'Student Name' },
  { key: 'courseName', label: 'Course Name' },
  { key: 'completionDate', label: 'Completion Date' },
  { key: 'certificateNumber', label: 'Certificate Number' },
  { key: 'grade', label: 'Grade / Score' },
  { key: 'batchNumber', label: 'Batch Number' },
  { key: 'enrollmentNumber', label: 'Enrollment Number' },
  { key: 'tenantName', label: 'Institution Name' },
  { key: 'issuerName', label: 'Issuer / Signatory' },
  { key: 'issuerTitle', label: 'Issuer Title' },
  { key: 'certificateTitle', label: 'Certificate Title' },
  { key: 'subtitle', label: 'Subtitle' },
];

function TemplateEditor({ template, onClose, onSaved, onError }) {
  const [form, setForm] = useState(null);
  const [mode, setMode] = useState('html');
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [selectedFieldId, setSelectedFieldId] = useState(null);
  const [dragging, setDragging] = useState(null);
  const [resizing, setResizing] = useState(null);
  const [rotating, setRotating] = useState(null);

  const canvasRef = useRef(null);

  useEffect(() => {
    if (template) {
      setForm({ ...template, fields: template.fields || [] });
      setMode(template.backgroundType === 'image' ? 'image' : 'html');
      setSelectedFieldId(null);
      setPreviewUrl(null);
    }
  }, [template]);

  useEffect(() => {
    if (!dragging) return;
    const onMove = (e) => {
      const dx = e.clientX - dragging.startX;
      const dy = e.clientY - dragging.startY;
      const pctX = (dx / dragging.rectW) * 100;
      const pctY = (dy / dragging.rectH) * 100;
      setForm(f => f ? ({
        ...f,
        fields: f.fields.map(fd => fd._id === dragging.id ? {
          ...fd,
          x: Math.max(0, Math.min(100, dragging.origX + pctX)),
          y: Math.max(0, Math.min(100, dragging.origY + pctY)),
        } : fd),
      }) : f);
    };
    const onUp = () => setDragging(null);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [dragging]);

  useEffect(() => {
    if (!resizing) return;
    const onMove = (e) => {
      const dx = e.clientX - resizing.startX;
      const dy = e.clientY - resizing.startY;
      const pctW = (dx / resizing.rectW) * 100;
      const pctH = (dy / resizing.rectH) * 100;
      setForm(f => f ? ({
        ...f,
        fields: f.fields.map(fd => fd._id === resizing.id ? {
          ...fd,
          w: Math.max(4, Math.min(100 - fd.x, resizing.origW + pctW)),
          h: Math.max(3, Math.min(100 - fd.y, resizing.origH + pctH)),
        } : fd),
      }) : f);
    };
    const onUp = () => setResizing(null);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [resizing]);

  useEffect(() => {
    if (!rotating) return;
    const onMove = (e) => {
      const angle = Math.atan2(e.clientY - rotating.centerY, e.clientX - rotating.centerX) * (180 / Math.PI);
      setForm(f => f ? ({
        ...f,
        fields: f.fields.map(fd => fd._id === rotating.id ? {
          ...fd,
          rotation: Math.round(angle),
        } : fd),
      }) : f);
    };
    const onUp = () => setRotating(null);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [rotating]);

  if (!template || !form) return null;

  const update = (patch) => setForm(f => ({ ...f, ...patch }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await putData(`/training/certificates/templates/${template._id}`, {
        name: form.name,
        description: form.description,
        orientation: form.orientation,
        widthPx: form.widthPx,
        heightPx: form.heightPx,
        backgroundType: mode,
        backgroundAsset: form.backgroundAsset,
        backgroundColor: form.backgroundColor,
        htmlBody: form.htmlBody,
        cssStyles: form.cssStyles,
        fields: form.fields,
        isDefault: form.isDefault,
      });
      onSaved(res.data || res);
    } catch (e) { onError(e.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const handlePublish = async () => {
    setPublishing(true);
    try {
      await postData(`/training/certificates/templates/${template._id}/publish`);
      onSaved(form);
    } catch (e) { onError(e.response?.data?.message || 'Publish failed'); }
    finally { setPublishing(false); }
  };

  const handlePreview = async () => {
    setPreviewing(true);
    try {
      // Save current state so preview renders the latest edits.
      // For published templates this forks a new draft — preview that one.
      const saved = await putData(`/training/certificates/templates/${template._id}`, {
        orientation: form.orientation,
        widthPx: form.widthPx,
        heightPx: form.heightPx,
        backgroundType: mode,
        backgroundAsset: form.backgroundAsset,
        backgroundColor: form.backgroundColor,
        htmlBody: form.htmlBody,
        cssStyles: form.cssStyles,
        fields: form.fields,
      });
      const updated = saved?.data || saved;
      const idToPreview = updated?._id || template._id;
      if (updated?._id && updated._id !== template._id) {
        // Switched to a new draft — caller (TemplatesTab) will refresh via onSaved, but
        // keep the editor pointed at the draft until close
        setForm(f => ({ ...f, ...updated }));
      }
      const res = await api.post(`/training/certificates/templates/${idToPreview}/preview`, {}, { responseType: 'blob' });
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(URL.createObjectURL(res.data));
    } catch (e) { onError(e.response?.data?.message || 'Preview failed'); }
    finally { setPreviewing(false); }
  };

  const handleBgUpload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    fd.append('backgroundType', mode);
    try {
      const res = await api.post(`/training/certificates/templates/${template._id}/upload-bg`, fd);
      const next = res.data?.data?.template || res.data?.template || res.data;
      if (next) {
        // Pull back the FULL template so we don't lose the auto-seeded
        // default field layout. Without this, form.fields stayed empty and
        // the next save wrote `fields: []`, producing blank certificates.
        setForm(f => ({
          ...f,
          backgroundAsset: next.backgroundAsset,
          backgroundType:  next.backgroundType || f.backgroundType,
          fields: (next.fields && next.fields.length) ? next.fields : (f.fields || []),
        }));
      }
    } catch (e) { onError(e.response?.data?.message || 'Upload failed'); }
  };

  const handlePrintPreview = () => {
    if (!previewUrl) return;
    const frame = window.open(previewUrl, '_blank', 'noopener,noreferrer');
    if (frame) {
      frame.onload = () => {
        try { frame.print(); } catch (_) {}
      };
    }
  };

  // Re-seed the dynamic field layout from sensible defaults. Useful when the
  // user wiped fields by accident or migrated from an older template.
  const resetFieldsToDefault = () => {
    if (form.fields?.length && !window.confirm(
      'Replace the current field layout with the default positions? Your current placements will be lost.'
    )) return;
    setForm(f => ({
      ...f,
      fields: [
        { _id: `seed-${Date.now()}-1`, key: 'studentName',       label: 'Student Name',       x: 15, y: 38, w: 70, h: 12, fontSize: 44, fontFamily: 'Georgia, serif',         fontWeight: 'bold',   fontStyle: 'normal', color: '#1a1a1a', align: 'center', lineHeight: 1.1, letterSpacing: 0, rotation: 0, isStatic: false },
        { _id: `seed-${Date.now()}-2`, key: 'courseName',        label: 'Course Name',        x: 20, y: 56, w: 60, h: 8,  fontSize: 24, fontFamily: 'Georgia, serif',         fontWeight: 'bold',   fontStyle: 'italic', color: '#444444', align: 'center', lineHeight: 1.2, letterSpacing: 0, rotation: 0, isStatic: false },
        { _id: `seed-${Date.now()}-3`, key: 'batchNumber',       label: 'Batch No.',          x: 12, y: 70, w: 28, h: 5,  fontSize: 14, fontFamily: 'Arial, sans-serif',      fontWeight: 'bold',   fontStyle: 'normal', color: '#1a1a1a', align: 'center', lineHeight: 1.2, letterSpacing: 0, rotation: 0, isStatic: false },
        { _id: `seed-${Date.now()}-4`, key: 'enrollmentNumber',  label: 'Enrollment No.',     x: 60, y: 70, w: 28, h: 5,  fontSize: 14, fontFamily: 'Arial, sans-serif',      fontWeight: 'bold',   fontStyle: 'normal', color: '#1a1a1a', align: 'center', lineHeight: 1.2, letterSpacing: 0, rotation: 0, isStatic: false },
        { _id: `seed-${Date.now()}-5`, key: 'completionDate',    label: 'Completion Date',    x: 12, y: 82, w: 25, h: 5,  fontSize: 14, fontFamily: 'Arial, sans-serif',      fontWeight: 'normal', fontStyle: 'normal', color: '#333333', align: 'center', lineHeight: 1.2, letterSpacing: 0, rotation: 0, isStatic: false },
        { _id: `seed-${Date.now()}-6`, key: 'certificateNumber', label: 'Certificate Number', x: 37, y: 90, w: 26, h: 4,  fontSize: 11, fontFamily: 'Courier New, monospace', fontWeight: 'normal', fontStyle: 'normal', color: '#555555', align: 'center', lineHeight: 1.2, letterSpacing: 1, rotation: 0, isStatic: false },
        { _id: `seed-${Date.now()}-7`, key: 'grade',             label: 'Grade',              x: 63, y: 82, w: 25, h: 5,  fontSize: 16, fontFamily: 'Arial, sans-serif',      fontWeight: 'bold',   fontStyle: 'normal', color: '#b68d40', align: 'center', lineHeight: 1.2, letterSpacing: 0, rotation: 0, isStatic: false },
      ],
    }));
  };

  const addField = (key) => {
    const label = FIELD_KEYS.find(k => k.key === key)?.label || key;
    setForm(f => ({
      ...f,
      fields: [...(f.fields || []), {
        _id: `new-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        type: 'text',
        key, label, x: 20, y: 45, w: 60, h: 10,
        fontSize: 24, fontFamily: 'Georgia, serif',
        fontWeight: 'bold', fontStyle: 'normal', color: '#1a1a1a', align: 'center', lineHeight: 1.2, letterSpacing: 0, rotation: 0,
        backgroundColor: 'transparent', borderRadius: 0, opacity: 1, zIndex: 2, srcKey: '', maxStars: 5, isStatic: false,
      }],
    }));
  };

  const addElement = (type) => {
    const stamp = `new-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const base = {
      _id: stamp,
      type,
      key: type === 'mask' ? `mask_${stamp.slice(-4)}` : type === 'image' ? 'tenantLogoUrl' : 'grade',
      label: type === 'mask' ? 'Mask Box' : type === 'image' ? 'Image Element' : 'Stars',
      x: 20, y: 20, w: type === 'mask' ? 20 : 28, h: type === 'mask' ? 10 : 8,
      fontSize: 24, fontFamily: 'Georgia, serif', fontWeight: 'bold', fontStyle: 'normal',
      color: type === 'stars' ? '#f59e0b' : '#1a1a1a', align: 'center', lineHeight: 1.2, letterSpacing: 0, rotation: 0,
      backgroundColor: type === 'mask' ? '#ffffff' : 'transparent', borderRadius: type === 'mask' ? 4 : 0, opacity: 1, zIndex: 2,
      srcKey: type === 'image' ? 'tenantLogoUrl' : '', maxStars: 5, isStatic: false,
    };
    setForm(f => ({ ...f, fields: [...(f.fields || []), base] }));
  };

  const addCustomField = () => {
    const label = window.prompt('Field label');
    if (!label) return;
    const rawKey = window.prompt('Field key (letters/numbers/underscore only)');
    if (!rawKey) return;
    const key = rawKey.trim().replace(/[^a-zA-Z0-9_]/g, '_');
    if (!key) return;
    setForm(f => ({
      ...f,
      fields: [...(f.fields || []), {
        _id: `new-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        type: 'text',
        key,
        label: label.trim(),
        x: 20, y: 45, w: 60, h: 10,
        fontSize: 24, fontFamily: 'Georgia, serif', fontWeight: 'bold', fontStyle: 'normal',
        color: '#1a1a1a', align: 'center', lineHeight: 1.2, letterSpacing: 0, rotation: 0,
        backgroundColor: 'transparent', borderRadius: 0, opacity: 1, zIndex: 2, srcKey: '', maxStars: 5, isStatic: false,
      }],
    }));
  };

  const updateField = (id, patch) => {
    setForm(f => ({ ...f, fields: f.fields.map(fd => fd._id === id ? { ...fd, ...patch } : fd) }));
  };

  const removeField = (id) => {
    setForm(f => ({ ...f, fields: f.fields.filter(fd => fd._id !== id) }));
    if (selectedFieldId === id) setSelectedFieldId(null);
  };

  // Drag handling on the canvas: convert pixel delta → percent of canvas.
  // The mousemove/mouseup listeners are set up in the hook above (must stay
  // above the early return for hooks-order safety).
  const handleMouseDown = (e, field) => {
    e.preventDefault();
    const rect = canvasRef.current.getBoundingClientRect();
    setSelectedFieldId(field._id);
    setDragging({
      id: field._id,
      startX: e.clientX,
      startY: e.clientY,
      origX: field.x,
      origY: field.y,
      rectW: rect.width,
      rectH: rect.height,
    });
  };

  const handleResizeMouseDown = (e, field) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = canvasRef.current.getBoundingClientRect();
    setSelectedFieldId(field._id);
    setResizing({
      id: field._id,
      startX: e.clientX,
      startY: e.clientY,
      origW: field.w,
      origH: field.h,
      rectW: rect.width,
      rectH: rect.height,
    });
  };

  const handleRotateMouseDown = (e, field) => {
    e.preventDefault();
    e.stopPropagation();
    const canvasRect = canvasRef.current.getBoundingClientRect();
    const centerX = canvasRect.left + ((field.x + (field.w / 2)) / 100) * canvasRect.width;
    const centerY = canvasRect.top + ((field.y + (field.h / 2)) / 100) * canvasRect.height;
    setSelectedFieldId(field._id);
    setRotating({
      id: field._id,
      centerX,
      centerY,
    });
  };

  const selectedField = form.fields.find(f => f._id === selectedFieldId);

  const bgUrl = form.backgroundAsset
    ? `${process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000'}${form.backgroundAsset}?t=${localStorage.getItem('token')}`
    : '';

  return (
    <BSModal show={!!template} onClose={onClose} title={`Edit Template — ${template.name}`} size="modal-xl"
      footer={<>
        <button className="btn btn-outline-secondary" onClick={handlePreview} disabled={previewing}>
          <i className="bi bi-eye me-1" />{previewing ? 'Rendering...' : 'Preview PDF'}
        </button>
        <button className="btn btn-outline-primary" onClick={handlePrintPreview} disabled={!previewUrl}>
          <i className="bi bi-printer me-1" />Print Preview
        </button>
        {template.status === 'draft' && (
          <button className="btn btn-outline-success" onClick={handlePublish} disabled={publishing}>
            <i className="bi bi-check-circle me-1" />{publishing ? 'Publishing...' : 'Publish'}
          </button>
        )}
        <button className="btn btn-secondary" onClick={onClose}>Close</button>
        <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : template.status === 'published' ? 'Save as New Version' : 'Save'}
        </button>
      </>}>
      <div className="row g-3">
        <div className="col-md-5">
          <div className="card border-0 bg-light mb-3">
            <div className="card-body p-3">
              <div className="mb-3">
                <label className="form-label small">Name</label>
                <input className="form-control form-control-sm" value={form.name || ''} onChange={e => update({ name: e.target.value })} />
              </div>
              <div className="mb-0">
                <label className="form-label small">Description</label>
                <textarea className="form-control form-control-sm" rows="2" value={form.description || ''} onChange={e => update({ description: e.target.value })} />
              </div>
            </div>
          </div>

          <div className="row g-2 mb-3">
            <div className="col-6">
              <label className="form-label small">Orientation</label>
              <select className="form-select form-select-sm" value={form.orientation}
                      onChange={e => update({ orientation: e.target.value,
                        widthPx: e.target.value === 'landscape' ? 1123 : 794,
                        heightPx: e.target.value === 'landscape' ? 794 : 1123 })}>
                <option value="landscape">Landscape</option>
                <option value="portrait">Portrait</option>
              </select>
            </div>
            <div className="col-3">
              <label className="form-label small">Width</label>
              <input type="number" className="form-control form-control-sm" value={form.widthPx} onChange={e => update({ widthPx: parseInt(e.target.value) || 1123 })} />
            </div>
            <div className="col-3">
              <label className="form-label small">Height</label>
              <input type="number" className="form-control form-control-sm" value={form.heightPx} onChange={e => update({ heightPx: parseInt(e.target.value) || 794 })} />
            </div>
          </div>

          <ul className="nav nav-pills mb-3">
            <li className="nav-item">
              <button className={`nav-link btn-sm ${mode === 'html' ? 'active' : ''}`} onClick={() => setMode('html')}>HTML</button>
            </li>
            <li className="nav-item">
              <button className={`nav-link btn-sm ${mode === 'image' ? 'active' : ''}`} onClick={() => setMode('image')}>Image Overlay</button>
            </li>
          </ul>

          {mode === 'html' && <>
            <div className="alert alert-info small py-2">
              Use Handlebars syntax: <code>{'{{studentName}}'}</code>, <code>{'{{courseName}}'}</code>, <code>{'{{completionDate}}'}</code>, <code>{'{{certificateNumber}}'}</code>, <code>{'{{grade}}'}</code>, <code>{'{{tenantName}}'}</code>, <code>{'{{qrDataUrl}}'}</code>, <code>{'{{verifyUrl}}'}</code>
            </div>
            <div className="mb-3">
              <label className="form-label small">HTML Body</label>
              <textarea className="form-control font-monospace small" rows="10" value={form.htmlBody || ''} onChange={e => update({ htmlBody: e.target.value })} />
            </div>
            <div className="mb-3">
              <label className="form-label small">CSS</label>
              <textarea className="form-control font-monospace small" rows="8" value={form.cssStyles || ''} onChange={e => update({ cssStyles: e.target.value })} />
            </div>
          </>}

          {mode === 'image' && <>
            <div className="mb-3">
              <label className="form-label small">Background Image / PDF</label>
              <input type="file" className="form-control form-control-sm"
                     accept="image/png,image/jpeg,image/webp,image/svg+xml,application/pdf"
                     onChange={e => handleBgUpload(e.target.files[0])} />
              {form.backgroundAsset && <div className="small text-muted mt-1">Current: {form.backgroundAsset.split('/').pop()}</div>}
            </div>

            {form.backgroundAsset && (!form.fields || form.fields.length === 0) && (
              <div className="alert alert-warning small py-2 mb-3">
                <i className="bi bi-exclamation-triangle me-1" />
                <strong>No fields yet.</strong> Certificates will render the background image but won't show any student data.
                <button type="button" className="btn btn-sm btn-warning ms-2" onClick={resetFieldsToDefault}>
                  <i className="bi bi-magic me-1" />Add default fields
                </button>
              </div>
            )}
            <div className="card border-0 bg-light mb-3">
              <div className="card-body p-3">
                <label className="form-label small">Add Elements</label>
                <div className="d-flex gap-2 align-items-end flex-wrap mb-2">
                  <div style={{ minWidth: 220, flex: '1 1 220px' }}>
                    <select className="form-select form-select-sm" value="" onChange={e => { if (e.target.value) addField(e.target.value); }}>
                    <option value="">+ Insert dynamic field...</option>
                    {FIELD_KEYS.map(k => <option key={k.key} value={k.key}>{k.label}</option>)}
                    </select>
                  </div>
                  <button type="button" className="btn btn-sm btn-outline-secondary" onClick={addCustomField}>
                    <i className="bi bi-plus-square me-1" />Custom Field
                  </button>
                  <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => addElement('mask')}>
                    <i className="bi bi-square-fill me-1" />Mask Box
                  </button>
                  <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => addElement('image')}>
                    <i className="bi bi-image me-1" />Image
                  </button>
                  <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => addElement('stars')}>
                    <i className="bi bi-stars me-1" />Stars
                  </button>
                  <button type="button" className="btn btn-sm btn-outline-secondary"
                          onClick={resetFieldsToDefault} title="Replace current fields with the default layout">
                    <i className="bi bi-arrow-clockwise me-1" />Reset Layout
                  </button>
                </div>
              </div>
            </div>

            {selectedField && (
              <div className="card bg-light mb-3">
                <div className="card-body p-2">
                  <div className="small fw-bold mb-2">Editing: {selectedField.label}</div>
                  <div className="row g-2">
                    <div className="col-6">
                      <label className="form-label small mb-0">Element Type</label>
                      <select className="form-select form-select-sm" value={selectedField.type || 'text'}
                              onChange={e => updateField(selectedField._id, { type: e.target.value })}>
                        <option value="text">Text</option>
                        <option value="mask">Mask Box</option>
                        <option value="image">Image</option>
                        <option value="stars">Stars</option>
                      </select>
                    </div>
                    <div className="col-6">
                      <label className="form-label small mb-0">Label</label>
                      <input className="form-control form-control-sm" value={selectedField.label || ''}
                             onChange={e => updateField(selectedField._id, { label: e.target.value })} />
                    </div>
                    <div className="col-6">
                      <label className="form-label small mb-0">Font Size</label>
                      <input type="number" className="form-control form-control-sm" value={selectedField.fontSize}
                             onChange={e => updateField(selectedField._id, { fontSize: parseInt(e.target.value) || 24 })} />
                    </div>
                    <div className="col-6">
                      <label className="form-label small mb-0">Color</label>
                      <input type="color" className="form-control form-control-color form-control-sm" value={selectedField.color}
                             onChange={e => updateField(selectedField._id, { color: e.target.value })} />
                    </div>
                    <div className="col-6">
                      <label className="form-label small mb-0">Background</label>
                      <input type="color" className="form-control form-control-color form-control-sm" value={(selectedField.backgroundColor && selectedField.backgroundColor !== 'transparent') ? selectedField.backgroundColor : '#ffffff'}
                             onChange={e => updateField(selectedField._id, { backgroundColor: e.target.value })} />
                    </div>
                    <div className="col-6">
                      <label className="form-label small mb-0">Opacity</label>
                      <input type="number" min="0" max="1" step="0.1" className="form-control form-control-sm" value={selectedField.opacity ?? 1}
                             onChange={e => updateField(selectedField._id, { opacity: parseFloat(e.target.value) || 0 })} />
                    </div>
                    <div className="col-6">
                      <label className="form-label small mb-0">Font Family</label>
                      <select className="form-select form-select-sm" value={selectedField.fontFamily}
                              onChange={e => updateField(selectedField._id, { fontFamily: e.target.value })}>
                        <option value="Georgia, serif">Georgia</option>
                        <option value="Times New Roman, serif">Times New Roman</option>
                        <option value="Arial, sans-serif">Arial</option>
                        <option value="Inter, sans-serif">Inter</option>
                        <option value="Courier New, monospace">Courier New</option>
                      </select>
                    </div>
                    <div className="col-6">
                      <label className="form-label small mb-0">Weight</label>
                      <select className="form-select form-select-sm" value={selectedField.fontWeight}
                              onChange={e => updateField(selectedField._id, { fontWeight: e.target.value })}>
                        <option value="normal">Normal</option>
                        <option value="bold">Bold</option>
                      </select>
                    </div>
                    <div className="col-6">
                      <label className="form-label small mb-0">Style</label>
                      <select className="form-select form-select-sm" value={selectedField.fontStyle || 'normal'}
                              onChange={e => updateField(selectedField._id, { fontStyle: e.target.value })}>
                        <option value="normal">Normal</option>
                        <option value="italic">Italic</option>
                      </select>
                    </div>
                    <div className="col-6">
                      <label className="form-label small mb-0">Align</label>
                      <select className="form-select form-select-sm" value={selectedField.align}
                              onChange={e => updateField(selectedField._id, { align: e.target.value })}>
                        <option value="left">Left</option>
                        <option value="center">Center</option>
                        <option value="right">Right</option>
                      </select>
                    </div>
                    <div className="col-3">
                      <label className="form-label small mb-0">Line</label>
                      <input type="number" step="0.1" className="form-control form-control-sm" value={selectedField.lineHeight || 1.2}
                             onChange={e => updateField(selectedField._id, { lineHeight: parseFloat(e.target.value) || 1.2 })} />
                    </div>
                    <div className="col-3">
                      <label className="form-label small mb-0">Space</label>
                      <input type="number" step="0.1" className="form-control form-control-sm" value={selectedField.letterSpacing || 0}
                             onChange={e => updateField(selectedField._id, { letterSpacing: parseFloat(e.target.value) || 0 })} />
                    </div>
                    <div className="col-3">
                      <label className="form-label small mb-0">Rotate</label>
                      <input type="number" className="form-control form-control-sm" value={Math.round(selectedField.rotation || 0)}
                             onChange={e => updateField(selectedField._id, { rotation: parseFloat(e.target.value) || 0 })} />
                    </div>
                    <div className="col-3">
                      <label className="form-label small mb-0">Radius</label>
                      <input type="number" className="form-control form-control-sm" value={Math.round(selectedField.borderRadius || 0)}
                             onChange={e => updateField(selectedField._id, { borderRadius: parseFloat(e.target.value) || 0 })} />
                    </div>
                    <div className="col-3">
                      <label className="form-label small mb-0">Z-Index</label>
                      <input type="number" className="form-control form-control-sm" value={Math.round(selectedField.zIndex || 1)}
                             onChange={e => updateField(selectedField._id, { zIndex: parseFloat(e.target.value) || 1 })} />
                    </div>
                    <div className="col-3">
                      <label className="form-label small mb-0">Static</label>
                      <select className="form-select form-select-sm" value={selectedField.isStatic ? 'yes' : 'no'}
                              onChange={e => updateField(selectedField._id, { isStatic: e.target.value === 'yes' })}>
                        <option value="no">Dynamic</option>
                        <option value="yes">Static</option>
                      </select>
                    </div>
                    {selectedField.isStatic && (
                      <div className="col-12">
                        <label className="form-label small mb-0">Static Text</label>
                        <input className="form-control form-control-sm" value={selectedField.label || ''}
                               onChange={e => updateField(selectedField._id, { label: e.target.value })} />
                      </div>
                    )}
                    {selectedField.type !== 'mask' && !selectedField.isStatic && (
                      <div className="col-12">
                        <label className="form-label small mb-0">Data Key</label>
                        <input className="form-control form-control-sm" value={selectedField.key || ''}
                               onChange={e => updateField(selectedField._id, { key: e.target.value.replace(/[^a-zA-Z0-9_]/g, '_') })} />
                      </div>
                    )}
                    {selectedField.type === 'image' && (
                      <div className="col-12">
                        <label className="form-label small mb-0">Image Source Key</label>
                        <input className="form-control form-control-sm" value={selectedField.srcKey || ''}
                               onChange={e => updateField(selectedField._id, { srcKey: e.target.value.replace(/[^a-zA-Z0-9_]/g, '_') })}
                               placeholder="e.g. tenantLogoUrl or signatureUrl" />
                      </div>
                    )}
                    {selectedField.type === 'stars' && (
                      <div className="col-12">
                        <label className="form-label small mb-0">Max Stars</label>
                        <input type="number" min="1" max="10" className="form-control form-control-sm" value={selectedField.maxStars || 5}
                               onChange={e => updateField(selectedField._id, { maxStars: parseInt(e.target.value, 10) || 5 })} />
                      </div>
                    )}
                    <div className="col-3">
                      <label className="form-label small mb-0">X%</label>
                      <input type="number" className="form-control form-control-sm" value={Math.round(selectedField.x)}
                             onChange={e => updateField(selectedField._id, { x: parseFloat(e.target.value) || 0 })} />
                    </div>
                    <div className="col-3">
                      <label className="form-label small mb-0">Y%</label>
                      <input type="number" className="form-control form-control-sm" value={Math.round(selectedField.y)}
                             onChange={e => updateField(selectedField._id, { y: parseFloat(e.target.value) || 0 })} />
                    </div>
                    <div className="col-3">
                      <label className="form-label small mb-0">W%</label>
                      <input type="number" className="form-control form-control-sm" value={Math.round(selectedField.w)}
                             onChange={e => updateField(selectedField._id, { w: parseFloat(e.target.value) || 40 })} />
                    </div>
                    <div className="col-3">
                      <label className="form-label small mb-0">H%</label>
                      <input type="number" className="form-control form-control-sm" value={Math.round(selectedField.h)}
                             onChange={e => updateField(selectedField._id, { h: parseFloat(e.target.value) || 8 })} />
                    </div>
                    <div className="col-12">
                      <button className="btn btn-outline-danger btn-sm w-100" onClick={() => removeField(selectedField._id)}>
                        <i className="bi bi-trash me-1" />Remove Field
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>}
        </div>

        <div className="col-md-7">
          <label className="form-label small">Canvas Preview</label>
          {mode === 'image' ? (
            <div ref={canvasRef}
                 style={{
                   position: 'relative',
                   width: '100%',
                   aspectRatio: `${form.widthPx} / ${form.heightPx}`,
                   background: bgUrl ? `url("${bgUrl}") center/cover no-repeat` : (form.backgroundColor || '#fff'),
                   border: '1px solid #ccc',
                   userSelect: 'none',
                   cursor: dragging ? 'grabbing' : 'default',
                 }}>
              {form.fields.map(field => (
                <div key={field._id}
                     onMouseDown={(e) => handleMouseDown(e, field)}
                     onClick={() => setSelectedFieldId(field._id)}
                     style={{
                       position: 'absolute',
                       left: `${field.x}%`, top: `${field.y}%`,
                       width: `${field.w}%`, height: `${field.h}%`,
                       display: 'flex',
                       alignItems: 'center',
                       justifyContent: field.align === 'left' ? 'flex-start' : field.align === 'right' ? 'flex-end' : 'center',
                       fontFamily: field.fontFamily,
                       fontSize: Math.max(10, (field.fontSize || 24) * 0.5),
                       fontWeight: field.fontWeight,
                       fontStyle: field.fontStyle || 'normal',
                       lineHeight: field.lineHeight || 1.2,
                       letterSpacing: `${field.letterSpacing || 0}px`,
                       color: field.color,
                       border: selectedFieldId === field._id ? '2px dashed #0d6efd' : '1px dashed rgba(0,0,0,0.3)',
                       background: field.type === 'mask'
                         ? (field.backgroundColor || '#ffffff')
                         : (selectedFieldId === field._id ? 'rgba(13,110,253,0.08)' : (field.backgroundColor || 'rgba(255,255,255,0.3)')),
                       cursor: 'move',
                       overflow: selectedFieldId === field._id ? 'visible' : 'hidden',
                       whiteSpace: 'pre-wrap',
                       textAlign: field.align || 'center',
                       transform: `rotate(${field.rotation || 0}deg)`,
                       borderRadius: field.borderRadius || 0,
                       opacity: field.opacity ?? 1,
                       zIndex: field.zIndex || 1,
                     }}>
                  {field.type === 'mask'
                    ? ''
                    : field.type === 'image'
                      ? <span className="small text-muted">{field.srcKey || field.key || 'image'}</span>
                      : field.type === 'stars'
                        ? '★★★★★'.slice(0, Math.max(1, Math.min(5, field.maxStars || 5)))
                        : (field.isStatic ? (field.label || 'Static Text') : `{{${field.key}}}`)}
                  {selectedFieldId === field._id && (
                    <>
                      <span
                        onMouseDown={(e) => handleResizeMouseDown(e, field)}
                        style={{
                          position: 'absolute',
                          right: -6,
                          bottom: -6,
                          width: 12,
                          height: 12,
                          borderRadius: 2,
                          background: '#0d6efd',
                          cursor: 'nwse-resize',
                        }}
                      />
                      <span
                        onMouseDown={(e) => handleRotateMouseDown(e, field)}
                        title="Rotate"
                        style={{
                          position: 'absolute',
                          left: '50%',
                          top: -22,
                          transform: 'translateX(-50%)',
                          width: 14,
                          height: 14,
                          borderRadius: '50%',
                          background: '#16a34a',
                          border: '2px solid #ffffff',
                          boxShadow: '0 0 0 1px rgba(0,0,0,0.15)',
                          cursor: 'grab',
                        }}
                      />
                    </>
                  )}
                </div>
              ))}
              {!form.fields.length && (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999' }}>
                  Upload a background and add fields to begin
                </div>
              )}
            </div>
          ) : (
            <div className="text-muted small py-3">
              HTML templates preview via the "Preview PDF" button below — the canvas editor here is for image overlays.
            </div>
          )}

          {previewUrl && (
            <div className="mt-3">
              <label className="form-label small">PDF Preview</label>
              <iframe src={previewUrl} title="PDF Preview" style={{ width: '100%', height: 400, border: '1px solid #ddd' }} />
            </div>
          )}
        </div>
      </div>
    </BSModal>
  );
}
